import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API_BASE, resolveMedia } from "@/lib/api";
import { printHtml, saleTicketHtml, boletaLegalHtml, renderTicketPng, qrDataUrl, receiptUrl, cashCloseHtml, contestVoucherHtml } from "@/lib/osPrint";
import { probeAgent, agentPrint, agentSystem } from "@/lib/localAgent";
import { Home, Monitor, AlertTriangle, ArrowLeft, ArrowRight, Plus, Minus, Trash2, CreditCard, Printer, CheckCircle2, ShoppingBag, X, Loader2, Sparkles, Hand, Lock, Volume2, VolumeX, Mic, Settings, Power, RotateCcw, FileText, UserCheck, Clock, ScanLine, Ticket, Gift, ShieldAlert, Search as SearchIcon } from "lucide-react";
import { useCedulaScan } from "@/lib/useCedulaScan";
import confetti from "canvas-confetti";
import { ElegirModificadores } from "@/components/ModificadoresDialog";
import { tbkVenta, tbkConectar, tbkTotales, tbkCierre } from "@/lib/tbkPos";
import { c2cCobrar } from "@/lib/tbkC2c";

/**
 * Autoservicio (Kiosco). Bootstrap por slug (/k/:slug).
 * — Sin login. Muestra catálogo, carrito lateral, escáner HID, pago simulado y ticket.
 * — Filtrado por `visible_autoservicio`.
 */
const clp = (n) => "$" + (Number(n || 0)).toLocaleString("es-CL");
const lineKey = (x) => x.lk || x.product_id;
const TP0 = { open: false, codigo: "", pin: "", err: null, busy: false };
// Teclado de la clave con números en posiciones al azar (nadie la adivina mirando)
const teclasAzar = () => {
  const d = "0123456789".split("");
  for (let i = d.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [d[i], d[j]] = [d[j], d[i]]; }
  return [...d.slice(0, 9), "del", d[9], "ok"];
};

const AVISO_TONOS = {
  error: { ring: "border-rose-400/40", bg: "bg-rose-500/15", ic: "text-rose-300", btn: "bg-rose-500 hover:bg-rose-400 text-white" },
  warn: { ring: "border-amber-400/40", bg: "bg-amber-400/15", ic: "text-amber-300", btn: "bg-amber-400 hover:bg-amber-300 text-slate-900" },
  info: { ring: "border-sky-400/40", bg: "bg-sky-500/15", ic: "text-sky-300", btn: "bg-sky-500 hover:bg-sky-400 text-white" },
};

/** Aviso propio del autoservicio (reemplaza los alert del navegador). */
function KioskAviso({ aviso, onClose }) {
  const tn = AVISO_TONOS[aviso.tono || "error"];
  const acciones = aviso.acciones?.length ? aviso.acciones : [{ label: "Aceptar" }];
  return (
    <div className="fixed inset-0 bg-slate-950/90 backdrop-blur-sm z-[95] grid place-items-center p-6 print:hidden" data-testid="kiosk-aviso">
      <div className={`bg-slate-900 border-2 ${tn.ring} rounded-3xl p-8 max-w-md w-full text-center animate-in fade-in zoom-in-95 duration-200`}>
        <div className={`mx-auto w-20 h-20 rounded-3xl ${tn.bg} grid place-items-center`}>
          {aviso.tono === "info" ? <CheckCircle2 className={`w-11 h-11 ${tn.ic}`} /> : <AlertTriangle className={`w-11 h-11 ${tn.ic}`} />}
        </div>
        <h3 data-testid="kiosk-aviso-titulo" className="mt-4 text-2xl font-black text-white font-display">{aviso.titulo}</h3>
        {aviso.mensaje && <p data-testid="kiosk-aviso-mensaje" className="mt-2 text-slate-300 whitespace-pre-line">{aviso.mensaje}</p>}
        <div className={`mt-6 grid gap-3 ${acciones.length > 1 ? "grid-cols-2" : ""}`}>
          {acciones.map((a, i) => (
            <button key={a.label} data-testid={`kiosk-aviso-btn-${i}`}
              onClick={() => { onClose(); a.onClick?.(); }}
              className={`min-h-14 px-3 py-2 rounded-2xl font-black text-base leading-tight ${i === acciones.length - 1 ? tn.btn : "border border-white/10 text-slate-200 hover:bg-white/5"}`}>
              {a.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

/** Tarjeta de producto memorizada: tocar un producto no vuelve a dibujar toda la grilla. */
const ProductCard = React.memo(function ProductCard({ p, onPick }) {
  return (
    <button
      data-testid={`kiosk-product-${p.sku}`}
      onClick={(e) => onPick(p, e.currentTarget)}
      className="text-left rounded-3xl bg-white/5 border border-white/10 overflow-hidden hover:bg-white/10 hover:border-sky-400/40 active:scale-[0.97] transition-[transform,background-color,border-color] duration-150 will-change-transform"
    >
      {p.image_url ? (
        <div className="aspect-square bg-white overflow-hidden">
          <img src={resolveMedia(p.image_url)} alt={p.nombre} loading="lazy" decoding="async"
            width="400" height="400" className="w-full h-full object-cover" />
        </div>
      ) : (
        <div className="aspect-square bg-gradient-to-br from-slate-800 to-slate-900 grid place-items-center">
          <Monitor className="w-16 h-16 text-slate-600" />
        </div>
      )}
      <div className="p-3 sm:p-4">
        <p className="font-bold text-white line-clamp-2 min-h-[3rem] text-sm sm:text-base">{p.nombre}</p>
        <p className="text-xl sm:text-2xl font-extrabold text-sky-300 mt-1.5 font-mono-num">{clp(p.precio)}</p>
      </div>
    </button>
  );
});

/** Esqueletos de carga: el kiosco nunca muestra una pantalla vacía. */
const GridSkeleton = () => (
  <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="kiosk-grid-skeleton">
    {Array.from({ length: 8 }).map((_, i) => (
      <div key={i} className="rounded-3xl bg-white/5 border border-white/10 overflow-hidden animate-pulse">
        <div className="aspect-square bg-white/5" />
        <div className="p-4 space-y-2">
          <div className="h-4 rounded bg-white/10" />
          <div className="h-4 w-2/3 rounded bg-white/10" />
          <div className="h-7 w-1/2 rounded bg-sky-500/20" />
        </div>
      </div>
    ))}
  </div>
);

const CACHE_KEY = (slug) => `aipos_kiosk_cache_${slug}`;

export default function KioskBootstrap() {
  const { slug } = useParams();
  const [config, setConfig] = useState(null);
  const [catalog, setCatalog] = useState({ products: [], categories: [] });
  const [promotions, setPromotions] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const publicApi = useMemo(() => axios.create({ baseURL: API_BASE }), []);
  // Sincronización en vivo: se consulta solo la huella y el catálogo se reemplaza
  // únicamente cuando el tótem está en reposo (nunca en medio de un pedido).
  const busyRef = useRef(false);
  const verRef = useRef("");

  useEffect(() => {
    let vivo = true;
    const tick = async () => {
      if (!vivo || busyRef.current) return;
      try {
        const v = (await publicApi.get(`/public/kiosk/${slug}/catalog-version`)).data?.version || "";
        if (!vivo || !v || v === verRef.current) return;
        const [c, p, pr] = await Promise.all([
          publicApi.get(`/public/kiosk-config/${slug}`),
          publicApi.get(`/public/kiosk/${slug}/products`),
          publicApi.get(`/public/kiosk/${slug}/promotions`).catch(() => ({ data: [] })),
        ]);
        if (!vivo || busyRef.current) return;
        verRef.current = v;
        setConfig(c.data); setCatalog(p.data); setPromotions(pr.data || []);
        try {
          localStorage.setItem(CACHE_KEY(slug), JSON.stringify({
            config: c.data, catalog: p.data, promotions: pr.data || [], at: Date.now(), version: v,
          }));
        } catch (_) { /* sin espacio */ }
      } catch (_) { /* sin red: el tótem sigue con su copia local */ }
    };
    const id = setInterval(tick, 60000);
    return () => { vivo = false; clearInterval(id); };
  }, [slug, publicApi]);

  useEffect(() => {
    let vivo = true;
    // 1) Arranque instantáneo con el catálogo guardado en el equipo
    try {
      const raw = localStorage.getItem(CACHE_KEY(slug));
      if (raw) {
        const cached = JSON.parse(raw);
        if (cached?.config && cached?.catalog) {
          setConfig(cached.config);
          setCatalog(cached.catalog);
          setPromotions(cached.promotions || []);
          verRef.current = cached.version || "";
          setLoading(false);
        }
      }
    } catch (_) { /* caché corrupta: se ignora */ }
    // 2) Revalidación en segundo plano
    (async () => {
      try {
        const [c, p, pr, vv] = await Promise.all([
          publicApi.get(`/public/kiosk-config/${slug}`),
          publicApi.get(`/public/kiosk/${slug}/products`),
          publicApi.get(`/public/kiosk/${slug}/promotions`).catch(() => ({ data: [] })),
          publicApi.get(`/public/kiosk/${slug}/catalog-version`).catch(() => ({ data: {} })),
        ]);
        if (!vivo) return;
        setConfig(c.data);
        setCatalog(p.data);
        setPromotions(pr.data || []);
        setError(null);
        verRef.current = vv.data?.version || "";
        try {
          localStorage.setItem(CACHE_KEY(slug), JSON.stringify({
            config: c.data, catalog: p.data, promotions: pr.data || [], at: Date.now(),
            version: verRef.current,
          }));
        } catch (_) { /* sin espacio: el kiosco sigue funcionando */ }
      } catch (e) {
        if (vivo && !localStorage.getItem(CACHE_KEY(slug))) {
          setError(e?.response?.data?.detail || "Kiosco no configurado");
        }
      } finally {
        if (vivo) setLoading(false);
      }
    })();
    return () => { vivo = false; };
  }, [slug, publicApi]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 text-white p-6" data-testid="kiosk-loading">
        <div className="h-10 w-64 rounded-2xl bg-white/5 animate-pulse mb-8" />
        <GridSkeleton />
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen bg-slate-950 text-white grid place-items-center p-6" data-testid="kiosk-bootstrap-error">
        <div className="max-w-md text-center">
          <div className="mx-auto w-16 h-16 rounded-full bg-amber-500/15 grid place-items-center mb-4">
            <AlertTriangle className="w-8 h-8 text-amber-400" />
          </div>
          <h1 className="text-2xl font-bold font-display">Kiosco no configurado</h1>
          <p className="mt-2 text-slate-400">{error}</p>
          <p className="mt-4 text-xs text-slate-500 font-mono-num">slug: {slug}</p>
          <Link to="/" className="mt-6 inline-flex items-center gap-1.5 text-sky-400 hover:text-sky-300 text-sm">
            <ArrowLeft className="w-4 h-4" /> Volver al inicio
          </Link>
        </div>
      </div>
    );
  }
  return <KioskShop config={config} catalog={catalog} promotions={promotions} slug={slug} api={publicApi} busyRef={busyRef} />;
}

function KioskShop({ config, catalog, promotions, slug, api, busyRef }) {
  const [started, setStarted] = useState(false);
  // El mismo toque que quita "Toca para comenzar" no debe agregar el producto que estaba debajo
  const startedAtRef = useRef(0);
  const [cart, setCart] = useState([]);
  const lastAddRef = useRef(null);
  const flyOriginRef = useRef(null);
  const cartAnchorRef = useRef(null);
  const [flyers, setFlyers] = useState([]);
  const [clearAskOpen, setClearAskOpen] = useState(false);
  const [cat, setCat] = useState("ALL");
  const [payOpen, setPayOpen] = useState(false);
  const [tbkEstado, setTbkEstado] = useState(null);
  const tbkPendRef = useRef(null);
  const c2cCancelRef = useRef(false);   // cobro Transbank aprobado aún sin venta registrada
  const [tp, setTp] = useState(TP0);
  const [processing, setProcessing] = useState(false);
  const [aviso, setAviso] = useState(null);
  const checkoutRef = useRef(null);
  const abrirPagoRef = useRef(null);
  const [lastSale, setLastSale] = useState(null);
  const [ticketOpen, setTicketOpen] = useState(false);
  const scanBufferRef = useRef("");
  const scanTimer = useRef(null);
  // Idioma: es-CL | en-US
  const [lang, setLang] = useState(() => localStorage.getItem("kiosk_lang") || "es");
  // Voz: enable/disable + tipo (índice del voice array)
  // Audio: el administrador define si el tótem habla y cómo arranca; el operador puede
  // cambiarlo durante el turno con el botón de la barra superior.
  const audioPermitido = config.kiosk_audio_enabled !== false;
  const [voiceOn, setVoiceOn] = useState(() => localStorage.getItem("kiosk_voice_on") !== "0");
  const [voices, setVoices] = useState([]);
  const [voiceURI, setVoiceURI] = useState(() => localStorage.getItem("kiosk_voice_uri") || "");
  const [voiceOpen, setVoiceOpen] = useState(false);
  // Contraseña salida
  const [exitOpen, setExitOpen] = useState(false);
  const [exitPin, setExitPin] = useState("");
  const [exitError, setExitError] = useState("");
  // Inactividad (segundos configurables por terminal) + cuenta atrás visible
  const idleTimerRef = useRef(null);
  const idleSeconds = Math.max(15, Number(config.kiosk_idle_seconds || 90));
  const [idleLeft, setIdleLeft] = useState(idleSeconds);
  // Operador del autoservicio (PIN)
  const [operator, setOperator] = useState(() => {
    try { return JSON.parse(localStorage.getItem(`kiosk_op_${slug}`) || "null"); } catch (_) { return null; }
  });
  // Menú de administración (5 toques en la esquina superior izquierda)
  const cornerTapsRef = useRef([]);
  const [adminPinOpen, setAdminPinOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [adminPin, setAdminPin] = useState("");
  const [adminError, setAdminError] = useState("");
  const [adminBusy, setAdminBusy] = useState(false);
  const [dayClose, setDayClose] = useState(null);
  // Estado de la impresora (sin papel / error) vía agente local
  const [printerAlert, setPrinterAlert] = useState(null);

  const products = useMemo(() => catalog.products || [], [catalog.products]);
  const categories = useMemo(() => catalog.categories || [], [catalog.categories]);

  useEffect(() => { localStorage.setItem("kiosk_lang", lang); }, [lang]);
  // El catálogo solo se reemplaza cuando el tótem está en reposo (sin pedido en curso)
  useEffect(() => {
    if (busyRef) busyRef.current = started || cart.length > 0;
  }, [busyRef, started, cart.length]);
  useEffect(() => { localStorage.setItem("kiosk_voice_on", voiceOn ? "1" : "0"); if (!voiceOn && window.speechSynthesis) window.speechSynthesis.cancel(); }, [voiceOn]);
  useEffect(() => { localStorage.setItem("kiosk_voice_uri", voiceURI || ""); }, [voiceURI]);

  // Cargar voces disponibles del navegador
  useEffect(() => {
    if (!window.speechSynthesis) return;
    const load = () => {
      const list = window.speechSynthesis.getVoices() || [];
      setVoices(list);
      // Si aún no hay voz elegida, sugerir la primera que coincida con el idioma
      if (!voiceURI && list.length) {
        const prefix = lang === "es" ? "es" : "en";
        const match = list.find((v) => (v.lang || "").toLowerCase().startsWith(prefix));
        if (match) setVoiceURI(match.voiceURI);
      }
    };
    load();
    window.speechSynthesis.onvoiceschanged = load;
    return () => { if (window.speechSynthesis) window.speechSynthesis.onvoiceschanged = null; };
  }, [lang, voiceURI]);

  // Diccionario i18n mínimo
  const t = useMemo(() => ({
    welcome_tap: lang === "es" ? "Toca para comenzar" : "Tap to start",
    welcome_sub: lang === "es" ? "Elige tus productos y paga en la pantalla · sin filas" : "Pick your products and pay on screen · no lines",
    welcome_active: lang === "es" ? "Sistema activo" : "System active",
    heading: lang === "es" ? "Toca un producto para comprar" : "Tap a product to buy",
    sub: lang === "es" ? "O acércalo al lector de código de barras." : "Or bring it to the barcode scanner.",
    all: lang === "es" ? "Todos" : "All",
    my_order: lang === "es" ? "Mi orden" : "My cart",
    empty_cart: lang === "es" ? "Carrito vacío" : "Empty cart",
    clear: lang === "es" ? "Vaciar" : "Clear",
    clear_ask_title: lang === "es" ? "¿Vaciar el carro?" : "Empty the cart?",
    clear_ask_body: lang === "es" ? "Se quitarán todos los productos que agregaste." : "All added products will be removed.",
    clear_ask_yes: lang === "es" ? "Sí, vaciar" : "Yes, empty",
    clear_ask_no: lang === "es" ? "No, seguir comprando" : "No, keep shopping",
    pay_right: lang === "es" ? "El terminal de pago está a tu derecha" : "The payment terminal is on your right",
    iva_included: lang === "es" ? "IVA 19% incluido" : "19% VAT included",
    subtotal: lang === "es" ? "Subtotal" : "Subtotal",
    promo_discount: lang === "es" ? "Descuento promociones" : "Promotions discount",
    total: lang === "es" ? "Total" : "Total",
    pay: lang === "es" ? "Pagar" : "Pay",
    total_to_pay: lang === "es" ? "Total a pagar" : "Total to pay",
    bring_card: lang === "es" ? "Acerca tu tarjeta al POS" : "Tap your card on the POS",
    sim_note: lang === "es" ? "(Simulación — se emitirá boleta al confirmar.)" : "(Simulation — receipt on confirm.)",
    cancel: lang === "es" ? "Cancelar" : "Cancel",
    confirm: lang === "es" ? "Confirmar" : "Confirm",
    payment_approved: lang === "es" ? "¡Pago aprobado!" : "Payment approved!",
    take_receipt: lang === "es" ? "Retira tu ticket y tus productos." : "Take your receipt and products.",
    print: lang === "es" ? "Imprimir" : "Print",
    continue_: lang === "es" ? "Continuar" : "Continue",
    autoservice: lang === "es" ? "Autoservicio" : "Self-service",
    promotions: lang === "es" ? "Promociones" : "Promotions",
    error_generic: lang === "es" ? "Error al procesar pago" : "Payment processing error",
    exit_title: lang === "es" ? "Salir del modo autoservicio" : "Exit self-service mode",
    exit_prompt: lang === "es" ? "Ingresa la contraseña de administrador" : "Enter admin password",
    exit_wrong: lang === "es" ? "PIN incorrecto" : "Wrong PIN",
    v_added: (name) => String(name || ""),
    v_pay_right: lang === "es" ? "El terminal de pago está a tu derecha" : "The payment terminal is on your right",
    v_removed: (name) => lang === "es" ? `Removiste ${name}` : `Removed ${name}`,
    v_total: (n) => lang === "es" ? `Total ${n} pesos` : `Total ${n} pesos`,
    v_welcome: (n) => lang === "es" ? `Bienvenido a ${n}` : `Welcome to ${n}`,
    v_approved: lang === "es" ? "Pago aprobado. Retira tu comprobante y productos." : "Payment approved. Take your receipt and products.",
    v_not_found: lang === "es" ? "Producto no encontrado. Pide ayuda en la caja." : "Product not found. Please ask for help.",
    not_found_title: lang === "es" ? "Producto no encontrado" : "Product not found",
    not_found_body: lang === "es"
      ? "No pudimos reconocer este código. Inténtalo de nuevo o pide ayuda en la caja."
      : "We could not recognize this code. Try again or ask for help at the counter.",
    accept: lang === "es" ? "Aceptar" : "OK",
    coupon: lang === "es" ? "Cupón" : "Coupon",
    coupon_cta: lang === "es" ? "Tengo un cupón" : "I have a coupon",
    coupon_title: lang === "es" ? "Ingresa tu cupón" : "Enter your coupon",
    coupon_hint: lang === "es" ? "Escríbelo o escanéalo con el lector" : "Type it or scan it with the reader",
    coupon_invalid: lang === "es" ? "Cupón no válido" : "Invalid coupon",
    apply: lang === "es" ? "Aplicar" : "Apply",
    remove: lang === "es" ? "Quitar" : "Remove",
    member_cta: lang === "es" ? "Soy cliente con puntos" : "I collect points",
    member_title: lang === "es" ? "Ingresa tu RUT" : "Enter your ID number",
    member_points: lang === "es" ? "Puntos acumulados" : "Points balance",
    member_earn: lang === "es" ? "Ganas con esta compra" : "You earn with this purchase",
    member_not_found: lang === "es" ? "Te inscribiremos con esta compra" : "We will enroll you with this purchase",
    quantity_saving: lang === "es" ? "Precio por cantidad" : "Quantity price",
  }), [lang]);

  // ======== VOZ (TTS) ========
  // Voz por defecto: "Google español de Estados Unidos". El operador NO puede cambiarla
  // ni silenciarla desde el kiosco (requisito §13.2 / §14): se configura desde administración.
  const pickDefaultVoice = useCallback(() => {
    const list = window.speechSynthesis?.getVoices?.() || [];
    if (!list.length) return null;
    const esList = list.filter((v) => /^es/i.test(v.lang || ""));
    return (
      esList.find((v) => /google/i.test(v.name) && /es[-_]us/i.test(v.lang)) ||
      esList.find((v) => /google/i.test(v.name) && /estados unidos|united states/i.test(v.name)) ||
      esList.find((v) => /google/i.test(v.name)) ||
      esList.find((v) => /es[-_]us/i.test(v.lang)) ||
      esList[0] ||
      null
    );
  }, []);

  const speak = useCallback((text) => {
    if (!voiceOn || !audioPermitido) return;
    if (!window.speechSynthesis || !text) return;
    try {
      const u = new SpeechSynthesisUtterance(String(text));
      u.lang = lang === "es" ? "es-CL" : "en-US";
      u.rate = 1.05;
      u.pitch = 1.0;
      let v = null;
      if (voiceURI) {
        const list = window.speechSynthesis.getVoices() || [];
        v = list.find((x) => x.voiceURI === voiceURI) || null;
      }
      if (!v && lang === "es") v = pickDefaultVoice();
      if (v) { u.voice = v; u.lang = v.lang; }
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(u);
    } catch (_) { /* ignorar */ }
  }, [lang, voiceOn, voiceURI, pickDefaultVoice, audioPermitido]);

  // Arranque del tótem: toma el estado de audio y la voz definidos en el terminal
  const audioBootRef = useRef(false);
  useEffect(() => {
    if (audioBootRef.current || !config.terminal_id) return;
    audioBootRef.current = true;
    if (config.kiosk_audio_enabled === false) { setVoiceOn(false); return; }
    setVoiceOn(config.kiosk_audio_on_start !== false);
    if (config.kiosk_voice_uri && !localStorage.getItem("kiosk_voice_uri")) {
      setVoiceURI(config.kiosk_voice_uri);
    }
  }, [config.terminal_id, config.kiosk_audio_enabled, config.kiosk_audio_on_start, config.kiosk_voice_uri]);

  // Qué dice al agregar un producto: nombre y precio, solo el nombre o solo el precio
  const sayProduct = useCallback((p) => {
    const modo = config.kiosk_speak_mode || "NOMBRE_PRECIO";
    const precio = `${Math.round(Number(p.precio) || 0)} pesos`;
    if (modo === "PRECIO") return precio;
    if (modo === "NOMBRE") return String(p.nombre || "");
    return `${p.nombre || ""}, ${precio}`;
  }, [config.kiosk_speak_mode]);

  // ======== PANTALLA COMPLETA + BLOQUEO ========
  const enterFullscreen = useCallback(async () => {
    try {
      if (!document.fullscreenElement && document.documentElement.requestFullscreen) {
        await document.documentElement.requestFullscreen({ navigationUI: "hide" });
      }
    } catch (_) { /* algunos navegadores rechazan sin gesto */ }
  }, []);
  const exitFullscreen = useCallback(async () => {
    try { if (document.fullscreenElement && document.exitFullscreen) await document.exitFullscreen(); } catch (_) {}
  }, []);

  // Bloqueo de teclas / menú del sistema mientras `started`
  useEffect(() => {
    if (!started) return;
    const blockKey = (e) => {
      // Bloquear atajos que sacan del kiosco
      const k = e.key;
      const blocked =
        k === "F11" || k === "F5" || k === "F12" ||
        (e.ctrlKey && ["r", "R", "w", "W", "t", "T", "n", "N", "p", "P", "u", "U", "s", "S", "h", "H", "j", "J"].includes(k)) ||
        (e.ctrlKey && e.shiftKey && ["I", "J", "C", "R", "N", "T"].includes(k)) ||
        (e.altKey && ["F4", "Tab", "Home"].includes(k)) ||
        (e.metaKey);
      if (blocked) { e.preventDefault(); e.stopPropagation(); return false; }
    };
    const blockContext = (e) => { e.preventDefault(); return false; };
    const blockUnload = (e) => { e.preventDefault(); e.returnValue = ""; return ""; };
    window.addEventListener("keydown", blockKey, true);
    window.addEventListener("contextmenu", blockContext, true);
    window.addEventListener("beforeunload", blockUnload);
    return () => {
      window.removeEventListener("keydown", blockKey, true);
      window.removeEventListener("contextmenu", blockContext, true);
      window.removeEventListener("beforeunload", blockUnload);
    };
  }, [started]);

  // ======== INACTIVIDAD (configurable por terminal) ========
  const resetIdle = useCallback(() => {
    if (idleTimerRef.current) clearInterval(idleTimerRef.current);
    if (!started) return;
    setIdleLeft(idleSeconds);
    idleTimerRef.current = setInterval(() => {
      setIdleLeft((n) => {
        if (n <= 1) {
          clearInterval(idleTimerRef.current);
          setCart([]); setPayOpen(false); setTicketOpen(false); setStarted(false);
          return 0;
        }
        return n - 1;
      });
    }, 1000);
  }, [started, idleSeconds]);
  useEffect(() => {
    if (!started) return;
    resetIdle();
    const bump = () => resetIdle();
    window.addEventListener("mousemove", bump);
    window.addEventListener("keydown", bump);
    window.addEventListener("touchstart", bump);
    window.addEventListener("click", bump);
    return () => {
      window.removeEventListener("mousemove", bump);
      window.removeEventListener("keydown", bump);
      window.removeEventListener("touchstart", bump);
      window.removeEventListener("click", bump);
      if (idleTimerRef.current) clearInterval(idleTimerRef.current);
    };
  }, [started, resetIdle]);

  const [promoVer, setPromoVer] = useState(null);   // pack combinable: muestra solo sus productos
  const filtered = useMemo(() => {
    if (promoVer) { const ids = new Set((promoVer.items || []).map((i) => i.product_id)); return products.filter((p) => ids.has(p.product_id)); }
    return products.filter((p) => cat === "ALL" || p.category_id === cat);
  }, [products, cat, promoVer]);

  // ======== GRILLA POR LOTES (catálogos de miles de SKU sin trabar la pantalla) ========
  const LOTE = 40;
  const [visibleCount, setVisibleCount] = useState(LOTE);
  const sentinelRef = useRef(null);
  const gridScrollRef = useRef(null);
  useEffect(() => { setVisibleCount(LOTE); }, [cat, products.length]);
  const visibles = useMemo(() => filtered.slice(0, visibleCount), [filtered, visibleCount]);
  // Carga progresiva: al acercarse al final de la lista se agrega el siguiente lote
  useEffect(() => {
    const cont = gridScrollRef.current;
    if (!cont) return;
    const onScroll = () => {
      if (cont.scrollTop + cont.clientHeight >= cont.scrollHeight - 900) {
        setVisibleCount((n) => (n < filtered.length ? n + LOTE : n));
      }
    };
    cont.addEventListener("scroll", onScroll, { passive: true });
    return () => cont.removeEventListener("scroll", onScroll);
  }, [filtered.length]);
  useEffect(() => {
    const nodo = sentinelRef.current;
    if (!nodo || visibleCount >= filtered.length) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) setVisibleCount((n) => n + LOTE);
    }, { root: gridScrollRef.current || null, rootMargin: "800px" });
    obs.observe(nodo);
    return () => obs.disconnect();
  }, [visibleCount, filtered.length]);

  // ======== PRESUPUESTO EN VIVO (promos, tramos y cupón calculados por el servidor) ========
  const [quote, setQuote] = useState(null);
  const [cupon, setCupon] = useState("");
  const [socio, setSocio] = useState(null);        // cliente fidelizado identificado por RUT
  const [cuponOpen, setCuponOpen] = useState(false);
  const [socioOpen, setSocioOpen] = useState(false);
  const [rutInput, setRutInput] = useState("");
  // Verificación de edad (alcohol, cigarrillos): cédula escaneada o PIN de supervisor
  const [ageOpen, setAgeOpen] = useState(false);
  const [ageAuth, setAgeAuth] = useState(null);
  const [ageMode, setAgeMode] = useState("CEDULA");
  // Buscador manual del tótem (teclado en pantalla)
  const [buscarOpen, setBuscarOpen] = useState(false);
  const [buscarQ, setBuscarQ] = useState("");
  const [agePin, setAgePin] = useState("");
  const [ageError, setAgeError] = useState("");
  const [ageBusy, setAgeBusy] = useState(false);
  const [scanError, setScanError] = useState(null); // producto no encontrado al escanear
  const [addedPill, setAddedPill] = useState(null); // confirmación visual al agregar
  const [modsPick, setModsPick] = useState(null);

  useEffect(() => {
    if (!cart.length) { setQuote(null); return; }
    const timer = setTimeout(() => {
      api.post(`/public/kiosk/${slug}/quote`, {
        items: cart.map((x) => ({ product_id: x.product_id, cantidad: x.cantidad, ...(x.mods_sel ? { modificadores: x.mods_sel } : {}) })),
        coupon_code: cupon.trim() || null,
        customer_rut: socio?.rut || null,
      }).then((r) => setQuote(r.data)).catch(() => setQuote(null));
    }, 220);
    return () => clearTimeout(timer);
  }, [cart, cupon, socio, api, slug]);

  const cuponOk = !!quote?.cupon?.valido;

  // El aviso de "producto no encontrado" se cierra solo a los 5 segundos
  useEffect(() => {
    if (!scanError) return;
    const to = setTimeout(() => setScanError(null), 5000);
    return () => clearTimeout(to);
  }, [scanError]);

  const rawTotal = cart.reduce((s, x) => s + x.precio * x.cantidad, 0);
  // Aplicar promociones para el display
  const promoDiscount = useMemo(() => {
    if (!promotions?.length || !cart.length) return 0;
    const qty = {};
    for (const it of cart) qty[it.product_id] = (qty[it.product_id] || 0) + it.cantidad;
    let disc = 0;
    for (const pr of promotions) {
      if (!pr.items?.length) continue;
      let fits = null;
      for (const pi of pr.items) {
        const av = qty[pi.product_id] || 0;
        const f = Math.floor(av / pi.cantidad);
        fits = fits === null ? f : Math.min(fits, f);
      }
      if (!fits || fits <= 0) continue;
      const regular = pr.items.reduce((s, pi) => {
        const p = products.find((x) => x.product_id === pi.product_id);
        return s + (p?.precio || 0) * pi.cantidad;
      }, 0);
      if ((pr.precio_bundle || 0) < regular) disc += (regular - pr.precio_bundle) * fits;
    }
    return disc;
  }, [promotions, cart, products]);
  const total = quote ? quote.total : Math.max(0, rawTotal - promoDiscount);
  const iva = Math.round(total - total / 1.19);

  // Cuando el usuario toca "Comenzar" → entra fullscreen + saluda por voz
  useEffect(() => {
    if (started) {
      enterFullscreen();
      speak(t.v_welcome(config.company_name || ""));
    } else {
      // Al volver a la bienvenida, cancelar TTS pendiente
      if (window.speechSynthesis) window.speechSynthesis.cancel();
    }
  }, [started, enterFullscreen, speak, t, config.company_name]);

  const addProduct = useCallback((p, qty = 1, mods = null) => {
    // Producto con opciones (sin tomate, agregar mayo…): el cliente las elige antes de agregarlo
    if (p.modificadores?.length && !mods) { setModsPick({ p, qty }); return; }
    const modsKey = mods?.sel?.length ? JSON.stringify(mods.sel) : "";
    const lk = modsKey ? `${p.product_id}|${modsKey}` : p.product_id;
    // §13.4 · Evita el doble agregado: un mismo producto no puede entrar dos veces
    // en menos de 900 ms (toque pendiente + escaneo posterior del mismo código).
    const now = Date.now();
    const last = lastAddRef.current;
    if (!mods && last && last.product_id === p.product_id && now - last.at < 900) return;
    lastAddRef.current = { product_id: p.product_id, at: now };
    // §13.5 · Animación "vuela al carro": la miniatura viaja desde la tarjeta hasta el carrito
    const origin = flyOriginRef.current || { x: window.innerWidth / 2, y: window.innerHeight / 2 };
    const anchor = cartAnchorRef.current?.getBoundingClientRect?.();
    const target = anchor
      ? { x: anchor.left + anchor.width / 2, y: anchor.top + anchor.height / 2 }
      : { x: window.innerWidth - 60, y: 80 };
    setFlyers((f) => [...f, {
      id: `${p.product_id}-${now}`,
      image_url: p.image_url,
      from: origin,
      dx: Math.round(target.x - origin.x),
      dy: Math.round(target.y - origin.y),
    }]);
    setTimeout(() => setFlyers((f) => f.filter((x) => x.id !== `${p.product_id}-${now}`)), 850);
    // Confirmación inmediata: píldora con el producto agregado
    setAddedPill({ id: now, nombre: p.nombre });
    setTimeout(() => setAddedPill((a) => (a && a.id === now ? null : a)), 1400);
    setCart((c) => {
      const ex = c.find((x) => lineKey(x) === lk);
      if (ex) return c.map((x) => lineKey(x) === lk ? { ...x, cantidad: +(x.cantidad + qty).toFixed(3) } : x);
      return [...c, { lk, product_id: p.product_id, nombre: p.nombre, sku: p.sku, precio: p.precio + (modsKey ? mods.extra || 0 : 0), cantidad: qty, image_url: p.image_url, requiere_autorizacion: !!p.requiere_autorizacion, edad_minima: Number(p.edad_minima) || 18,
        ...(modsKey ? { mods_sel: mods.sel, modificadores: mods.txt } : {}) }];
    });
    // La voz no debe bloquear el agregado: se dispara después del render
    setTimeout(() => speak(sayProduct(p)), 0);
  }, [speak, sayProduct]);
  const productById = useMemo(() => new Map(products.map((p) => [p.product_id, p])), [products]);
  // Toque en una tarjeta: referencia estable para que las tarjetas memorizadas no se redibujen
  const pickProduct = useCallback((p, nodo) => {
    if (Date.now() - startedAtRef.current < 700) return;
    if (nodo) {
      const r = nodo.getBoundingClientRect();
      flyOriginRef.current = { x: r.left + r.width / 2, y: r.top + r.height / 3 };
      // Sin foco pegado: si después se escanea otro producto, no se agrega este de nuevo
      try { nodo.blur(); } catch (_) { /* noop */ }
    }
    addProduct(p);
  }, [addProduct]);
  // Botón Inicio: vuelve a la pantalla de bienvenida (si hay carro, pide confirmar: se pierde)
  const [homeAsk, setHomeAsk] = useState(false);
  const irInicio = (confirmado) => {
    if (cart.length && confirmado !== true) { setHomeAsk(true); return; }
    setHomeAsk(false); setCart([]); setPayOpen(false); setTp(TP0); setPromoVer(null); setCat("ALL"); setStarted(false);
  };
  const esCombo = (pr) => (pr.tipo || "COMBO") === "COMBO";
  // Solo se ofrecen las promociones que este tótem puede vender (familias y catálogo de la sucursal)
  const promosKiosk = useMemo(() => (promotions || []).filter((pr) => esCombo(pr)
    ? (pr.items || []).every((it) => productById.get(it.product_id))
    : (pr.items || []).some((it) => productById.get(it.product_id))), [promotions, productById]);
  const applyPromo = (pr) => {
    if (!esCombo(pr)) { setPromoVer(pr); setCat("ALL"); return; }
    for (const it of pr.items) addProduct(productById.get(it.product_id), it.cantidad);
  };
  const inc = (id, d) => setCart((c) => c.map((x) => lineKey(x) === id ? { ...x, cantidad: Math.max(1, x.cantidad + d) } : x));
  const rm = (id) => setCart((c) => c.filter((x) => lineKey(x) !== id));
  // Resultados del buscador manual del tótem
  const resultadosBusqueda = useMemo(() => {
    const q = buscarQ.trim().toLowerCase();
    if (q.length < 2) return [];
    return (catalog?.products || []).filter((p) =>
      String(p.nombre || "").toLowerCase().includes(q)
      || String(p.sku || "").toLowerCase().includes(q)).slice(0, 12);
  }, [buscarQ, catalog]);

  const clearCart = () => setCart([]);
  // Productos del carro con venta restringida por edad (alcohol, cigarrillos)
  const itemsRestringidos = useMemo(() => cart.filter((x) => x.requiere_autorizacion), [cart]);
  const edadMinima = useMemo(
    () => Math.max(18, ...itemsRestringidos.map((x) => Number(x.edad_minima) || 18)),
    [itemsRestringidos],
  );
  useEffect(() => { if (itemsRestringidos.length === 0) setAgeAuth(null); }, [itemsRestringidos.length]);

  // Lector de cédula: pantalla de fidelización del tótem
  const lectorSocio = useCedulaScan({
    onCedula: async (ced) => {
      lectorSocio.limpiar();
      setRutInput((ced.rut || "").replace("-", ""));
      try {
        const lo = await api.get(`/public/kiosk/${slug}/loyalty?rut=${encodeURIComponent(ced.rut)}&total=${total}`);
        setSocio({ ...lo.data, nacimiento: ced.nacimiento || null });
        speak(lang === "es" ? "Cédula leída" : "ID card read");
      } catch (_) { setSocio({ valido: false }); }
    },
    onError: () => { lectorSocio.limpiar(); setSocio({ valido: false }); },
  });

  // Lector de cédula: autorización por edad antes de pagar
  const lectorEdad = useCedulaScan({
    onCedula: async (ced, crudo) => {
      lectorEdad.limpiar();
      setAgeBusy(true); setAgeError("");
      try {
        const r = await api.post(`/public/kiosk/${slug}/age-auth`,
                                 { modo: "CEDULA", scan: crudo, edad_minima: edadMinima });
        setAgeAuth(r.data);
        setAgeOpen(false);
        speak(lang === "es" ? "Autorización aprobada" : "Authorization approved");
        setTimeout(() => abrirPagoRef.current?.(), 1500);
      } catch (err) {
        setAgeError(err?.response?.data?.detail
          || (lang === "es" ? "No se pudo validar la cédula" : "Could not validate the ID"));
        speak(lang === "es" ? "No fue posible validar tu cédula" : "Could not validate your ID");
      } finally { setAgeBusy(false); }
    },
    onError: () => {
      lectorEdad.limpiar();
      setAgeError(lang === "es"
        ? "No se pudo leer la cédula. Inténtalo otra vez o llama a un supervisor"
        : "Could not read the ID. Try again or call a supervisor");
    },
  });

  // Índice de códigos para resolver el escaneo al instante, sin esperar al servidor
  const codeIndex = useMemo(() => {
    const m = new Map();
    for (const p of products) {
      if (p.barcode) m.set(String(p.barcode).trim(), p);
      if (p.sku) m.set(String(p.sku).trim().toUpperCase(), p);
    }
    return m;
  }, [products]);
  // Etiqueta de balanza interpretada en el equipo (misma regla que el servidor)
  const balanzaLocal = (code) => {
    const c = config.balanza;
    if (!c?.enabled || !/^\d+$/.test(code) || code.length !== Number(c.largo_total || 13)) return null;
    if (!(c.prefijos || []).some((p) => code.startsWith(String(p)))) return null;
    const sku = code.substr(Number(c.sku_start), Number(c.sku_length));
    const raw = parseInt(code.substr(Number(c.valor_start), Number(c.valor_length)), 10);
    const p = codeIndex.get(sku) || codeIndex.get(sku.replace(/^0+/, "") || "0");
    if (Number.isNaN(raw)) return null;
    return { p, cantidad: c.valor_tipo === "PRECIO" ? 1 : Math.round((raw / 10 ** Number(c.peso_decimales ?? 3)) * 1000) / 1000 };
  };

  // Escaneo global por HID (el lector envía caracteres + Enter)
  useEffect(() => {
    const onKey = (e) => {
      if (payOpen || ticketOpen) return;
      if (["INPUT", "TEXTAREA"].includes(document.activeElement?.tagName)) return;
      if (e.key === "Enter") {
        // El Enter del lector no debe "clickear" el producto que quedó con el foco
        e.preventDefault();
        if (document.activeElement && document.activeElement !== document.body) {
          try { document.activeElement.blur(); } catch (_) { /* noop */ }
        }
        const code = scanBufferRef.current.trim();
        scanBufferRef.current = "";
        if (code.length < 3) return;
        // Si el kiosco está en la pantalla de bienvenida, el escaneo lo despierta
        if (!started) setStarted(true);
        // 1) catálogo ya cargado → se agrega de inmediato
        const bal = balanzaLocal(code);
        if (bal?.p) { addProduct(bal.p, bal.cantidad); return; }
        const local = !bal && (codeIndex.get(code) || codeIndex.get(code.toUpperCase()) || codeIndex.get(code.replace(/^0+/, "")));
        if (local) { addProduct(local, 1); return; }
        // 2) catálogo y regla de balanza ya cargados: el código no existe → aviso inmediato
        if (products.length && config.balanza) { setScanError({ code }); speak(t.v_not_found); return; }
        // Respaldo mientras el catálogo aún carga
        api.get(`/public/kiosk/${slug}/scan?code=${encodeURIComponent(code)}`)
          .then((r) => {
            if (r.data?.found) addProduct(r.data.product, r.data.cantidad || 1);
            else { setScanError({ code }); speak(t.v_not_found); }
          })
          .catch(() => { setScanError({ code }); speak(t.v_not_found); });
        return;
      }
      if (e.key.length === 1) {
        scanBufferRef.current += e.key;
        if (scanTimer.current) clearTimeout(scanTimer.current);
        scanTimer.current = setTimeout(() => { scanBufferRef.current = ""; }, 400);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [slug, payOpen, ticketOpen, api, addProduct, codeIndex, started, products.length, config.balanza]);

  // ======== IMPRESIÓN (igual que una caja POS) ========
  const ticketLogo = config.print_logo ? (config.logo_url ? resolveMedia(config.logo_url) : "/brand/aipos-logo.png") : null;

  const reportEvent = useCallback((tipo, detalle) => {
    api.post(`/public/kiosk/${slug}/event`, { tipo, detalle }).catch(() => {});
  }, [api, slug]);

  const buildSaleHtml = useCallback(async (sale) => {
    const ctx = {
      cashier: operator?.nombre, caja: config.nombre,
      footer_message: config.ticket_footer || "",
      gracias_message: config.ticket_gracias || "",
    };
    if (config.boleta_print_mode === "BOLETA_PDF417") {
      try {
        const { data } = await api.get(`/public/receipt/${sale.sale_id}`);
        if (data?.dte?.pdf417) return boletaLegalHtml(data, { ...ctx, logo_url: ticketLogo });
      } catch (_) { /* cae al vale con QR */ }
    }
    const qr = await qrDataUrl(receiptUrl(sale.sale_id));
    return saleTicketHtml(sale, config.company_name, config.store_name, ticketLogo,
      config.ticket_footer, { ...ctx, qr });
  }, [api, config, operator, ticketLogo]);

  const printSale = useCallback(async (sale) => {
    if (!sale) return;
    const html = await buildSaleHtml(sale);
    try {
      if (config.agent_enabled) {
        // Agente local de Windows: impresión directa sin diálogo
        const png = await renderTicketPng(html, `Boleta ${sale.numero || ""}`);
        await agentPrint(config.agent_port, config.agent_token,
          png ? [{ type: "raster", data_base64: png }] : [],
          { openDrawer: false });
      } else {
        await printHtml(html, `Boleta ${sale.numero || ""}`);
      }
      // Vales de concurso: uno por cada concurso que aplicó a esta venta
      for (const v of (sale.contest_vouchers || [])) {
        const vqr = await qrDataUrl(v.voucher_code);
        const vhtml = contestVoucherHtml(v, { qr: vqr, caja: config.nombre, cashier: operator?.nombre });
        if (config.agent_enabled) {
          const vpng = await renderTicketPng(vhtml, `Vale ${v.voucher_code}`);
          await agentPrint(config.agent_port, config.agent_token,
            vpng ? [{ type: "raster", data_base64: vpng }] : [], { openDrawer: false });
        } else {
          await printHtml(vhtml, `Vale concurso ${v.voucher_code}`);
        }
      }
    } catch (e) {
      setPrinterAlert({ tipo: "ERROR", msg: String(e?.message || e) });
      reportEvent("IMPRESORA_ERROR", { sale_id: sale.sale_id, error: String(e?.message || e) });
    }
  }, [buildSaleHtml, config, reportEvent, operator]);

  // Vigila el estado de la impresora del agente: sin papel / error / fuera de línea
  useEffect(() => {
    if (!config.agent_enabled) return;
    let cancel = false;
    const check = async () => {
      const st = await probeAgent(config.agent_port);
      if (cancel) return;
      if (!st?.ok) {
        setPrinterAlert({ tipo: "AGENTE", msg: "El agente de impresión no responde" });
        return;
      }
      if (st.paper_out) {
        setPrinterAlert({ tipo: "SIN_PAPEL", msg: "La impresora se quedó sin papel" });
        reportEvent("IMPRESORA_SIN_PAPEL", { printer: st.printer });
      } else if (st.printer_error || st.offline) {
        setPrinterAlert({ tipo: "ERROR", msg: "La impresora tiene un error o está fuera de línea" });
        reportEvent("IMPRESORA_ERROR", { printer: st.printer, status: st.status_text });
      } else if (st.printer_found === false) {
        setPrinterAlert({ tipo: "ERROR", msg: "No se encuentra la impresora configurada" });
      } else {
        setPrinterAlert(null);
      }
    };
    check();
    const iv = setInterval(check, 60000);
    return () => { cancel = true; clearInterval(iv); };
  }, [config.agent_enabled, config.agent_port, reportEvent]);

  // Tarjeta Propia: QR de la tarjeta + clave de 4 dígitos (nadie vigila la compra)
  const pagarTP = async () => {
    if (!/^\d{20}$/.test(tp.codigo.replace(/\s/g, ""))) { setTp({ ...tp, err: lang === "es" ? "Escanea el código QR de tu tarjeta" : "Scan your card QR code" }); return; }
    if (!/^\d{4}$/.test(tp.pin)) { setTp({ ...tp, err: lang === "es" ? "Ingresa tu clave de 4 dígitos" : "Enter your 4-digit PIN" }); return; }
    setTp({ ...tp, busy: true, err: null });
    try {
      const { data } = await api.post(`/public/kiosk/${slug}/tarjeta-propia/autorizar`, { codigo: tp.codigo.replace(/\s/g, ""), pin: tp.pin, monto: total });
      await checkout({ payment_method: "TARJETA_PROPIA", credito_auth: data.credito_auth });
      setTp(TP0);
    } catch (e) { setTp({ ...tp, pin: "", busy: false, err: e?.response?.data?.detail || "No se pudo validar la tarjeta" }); }
  };

  const checkout = async (pago = {}) => {
    if (!cart.length) return;
    setProcessing(true);
    try {
      // POS Transbank integrado: el cliente paga en el equipo antes de registrar la venta
      if (config.tbk_enabled && !pago.payment_method) {
        // Un cobro ya aprobado por el mismo total se reutiliza: reintentar nunca cobra dos veces
        let pend = tbkPendRef.current;
        if (!pend || Math.abs(pend.total - total) > 0.5) {
          setTbkEstado(lang === "es" ? "Sigue las instrucciones del equipo Transbank…" : "Follow the Transbank terminal…");
          let tr;
          try { tr = await tbkVenta(total, String(Date.now()).slice(-6), setTbkEstado); }
          catch (e) { tr = { aprobado: false, mensaje: e?.message || "No se pudo conectar con el equipo Transbank" }; }
          setTbkEstado(null);
          if (!tr.aprobado) {
            const varios = !!config.tarjeta_propia;
            speak(lang === "es" ? "Pago rechazado. Puedes intentarlo nuevamente." : "Payment declined. Please try again.");
            setAviso({
              tono: "error",
              titulo: lang === "es" ? "Pago no realizado" : "Payment not completed",
              mensaje: `${tr.mensaje || ""}\n${lang === "es" ? "No se realizó ningún cargo a tu tarjeta." : "Your card was not charged."}`.trim(),
              acciones: [
                varios ? { label: lang === "es" ? "Otra forma de pago" : "Other method" }
                       : { label: lang === "es" ? "Volver" : "Back", onClick: () => setPayOpen(false) },
                { label: lang === "es" ? "Reintentar" : "Retry", onClick: () => setTimeout(() => checkoutRef.current?.(), 50) },
              ],
            });
            return;
          }
          pend = { tbk: tr, total, key: `kiosk-tbk-${slug}-${tr.autorizacion}-${Date.now()}` };
          tbkPendRef.current = pend;
        }
        pago = { ...pago, tbk: pend.tbk, idempotency_key: pend.key };
      } else if (config.c2c_enabled && !pago.payment_method) {
        // Transbank C2C: el cobro va por internet al equipo; un cobro aprobado por el mismo total se reutiliza
        let pend = tbkPendRef.current;
        if (!pend?.c2c || Math.abs(pend.total - total) > 0.5) {
          setTbkEstado(lang === "es" ? "Sigue las instrucciones del equipo Transbank…" : "Follow the Transbank terminal…");
          let cr;
          c2cCancelRef.current = false;
          try { cr = await c2cCobrar({ base: `/public/kiosk/${slug}/c2c`, monto: total, onEstado: setTbkEstado, client: api, cancelRef: c2cCancelRef }); }
          catch (e) { cr = { aprobado: false, mensaje: e?.response?.data?.detail || "No se pudo conectar con Transbank" }; }
          setTbkEstado(null);
          if (!cr.aprobado) {
            speak(lang === "es" ? "Pago rechazado. Puedes intentarlo nuevamente." : "Payment declined. Please try again.");
            setAviso({
              tono: "error",
              titulo: lang === "es" ? "Pago no realizado" : "Payment not completed",
              mensaje: `${cr.mensaje || ""}\n${lang === "es" ? "No se realizó ningún cargo a tu tarjeta." : "Your card was not charged."}`.trim(),
              acciones: [
                config.tarjeta_propia ? { label: lang === "es" ? "Otra forma de pago" : "Other method" }
                  : { label: lang === "es" ? "Volver" : "Back", onClick: () => setPayOpen(false) },
                { label: lang === "es" ? "Reintentar" : "Retry", onClick: () => setTimeout(() => checkoutRef.current?.(), 50) },
              ],
            });
            return;
          }
          pend = { c2c: cr.trace_id, total, key: `kiosk-c2c-${slug}-${cr.trace_id}` };
          tbkPendRef.current = pend;
        }
        pago = { ...pago, tbk_c2c_trace_id: pend.c2c, idempotency_key: pend.key };
      }
      const r = await api.post(`/public/kiosk/${slug}/checkout`, {
        items: cart.map((x) => (x.mods_sel ? { product_id: x.product_id, cantidad: x.cantidad, modificadores: x.mods_sel }
          : { product_id: x.product_id, cantidad: x.cantidad, precio_unitario: x.precio })),
        payment_method: "TARJETA",
        ...pago,
        operator_user_id: operator?.user_id || null,
        operator_name: operator?.nombre || null,
        coupon_code: cuponOk ? cupon.trim().toUpperCase() : null,
        customer_rut: socio?.valido && socio?.rut ? socio.rut : null,
        customer_name: socio?.valido ? (socio.nombre || "Cliente autoservicio") : null,
        customer_nacimiento: socio?.nacimiento || null,
        age_auth: ageAuth || null,
      });
      tbkPendRef.current = null;
      setLastSale(r.data.sale);
      setCart([]);
      setCupon(""); setSocio(null); setQuote(null);
      setAgeAuth(null);
      setPayOpen(false);
      setTicketOpen(true);
      speak(t.v_approved);
      // Impresión directa, sin vista previa: agente local o impresora del sistema
      printSale(r.data.sale);
      if (config.c2c_enabled && config.c2c_imprimir_boleta) api.post(`/public/kiosk/${slug}/c2c/imprimir/${r.data.sale.sale_id}`).catch(() => {});
      // 🎉 Confeti festivo al aprobar la venta
      const fire = (particleRatio, opts) => {
        confetti({
          origin: { y: 0.65 },
          spread: 90,
          startVelocity: 45,
          scalar: 1.1,
          ticks: 300,
          colors: ["#0EA5E9", "#F59E0B", "#10B981", "#EF4444", "#8B5CF6", "#EC4899", "#22D3EE", "#FACC15"],
          ...opts,
          particleCount: Math.floor(200 * particleRatio),
        });
      };
      fire(0.25, { spread: 26, startVelocity: 55 });
      fire(0.2, { spread: 60 });
      fire(0.35, { spread: 100, decay: 0.91, scalar: 0.9 });
      fire(0.1, { spread: 120, startVelocity: 25, decay: 0.92, scalar: 1.2 });
      fire(0.1, { spread: 120, startVelocity: 45 });
      // Segunda ráfaga a los 300ms
      setTimeout(() => {
        confetti({ particleCount: 100, angle: 60, spread: 55, origin: { x: 0 }, colors: ["#0EA5E9", "#F59E0B", "#10B981", "#EF4444", "#8B5CF6"] });
        confetti({ particleCount: 100, angle: 120, spread: 55, origin: { x: 1 }, colors: ["#0EA5E9", "#F59E0B", "#10B981", "#EF4444", "#8B5CF6"] });
      }, 350);
      // El aviso de compra aprobada se cierra solo y vuelve a la pantalla principal
      setTimeout(() => { setTicketOpen(false); setStarted(false); }, 9000);
    } catch (e) {
      if (pago.credito_auth) { setProcessing(false); throw e; }
      const det = e?.response?.data?.detail || "Error al procesar pago";
      setAviso(pago.tbk || pago.tbk_c2c_trace_id ? {
        tono: "warn",
        titulo: lang === "es" ? "Pago aprobado, falta registrar" : "Paid, not yet registered",
        mensaje: `${det}\n\n${lang === "es" ? `Tu pago fue aprobado${pago.tbk ? ` (aut. ${pago.tbk.autorizacion})` : ""}. Toca Registrar para terminar sin cobrarte de nuevo, o pide ayuda al personal.` : `Your payment was approved${pago.tbk ? ` (auth ${pago.tbk.autorizacion})` : ""}. Tap Register to finish without being charged again.`}`,
        acciones: [{ label: lang === "es" ? "Registrar compra" : "Register", onClick: () => setTimeout(() => checkoutRef.current?.(), 50) }],
      } : { tono: "error", titulo: lang === "es" ? "No se pudo completar" : "Could not complete", mensaje: det });
    } finally {
      setProcessing(false);
    }
  };

  checkoutRef.current = checkout;
  // Medios integrados del tótem: con uno solo se cobra directo, con varios el cliente elige
  const metodos = [...(config.tbk_enabled ? ["TBK"] : []), ...(config.tarjeta_propia ? ["TP"] : [])];
  const abrirPago = async () => {
    let bloqueado = config.ventas_bloqueadas;
    try { bloqueado = (await api.get(`/public/kiosk-config/${slug}`)).data.ventas_bloqueadas; } catch (_) { /* sin red: usa lo último */ }
    if (bloqueado) {
      setAviso({ tono: "warn", titulo: lang === "es" ? "Autoservicio no disponible" : "Self-checkout unavailable",
        mensaje: lang === "es" ? "Por ahora no podemos recibir pagos en este autoservicio. Por favor paga en caja." : "Please pay at the cashier." });
      return;
    }
    setPayOpen(true);
    const totalTxt = t.v_total(Number(total).toLocaleString(lang === "es" ? "es-CL" : "en-US"));
    if (metodos.length > 1) {
      speak(`${totalTxt}. ${lang === "es" ? "Selecciona tu forma de pago en la pantalla" : "Choose your payment method on screen"}`);
    } else if (metodos[0] === "TP") {
      setTp({ ...TP0, open: true });
      speak(`${totalTxt}. ${lang === "es" ? `Escanea tu ${config.tarjeta_propia} e ingresa tu clave` : "Scan your card and enter your PIN"}`);
    } else {
      speak(`${totalTxt}. ${t.v_pay_right}`);
      if (metodos[0] === "TBK") setTimeout(() => checkoutRef.current?.(), 300);
    }
  };

  abrirPagoRef.current = abrirPago;

  // ======== MENÚ DE ADMINISTRACIÓN (5 toques arriba a la izquierda) ========
  const onCornerTap = () => {
    const now = Date.now();
    cornerTapsRef.current = [...cornerTapsRef.current.filter((x) => now - x < 4000), now];
    if (cornerTapsRef.current.length >= 5) {
      cornerTapsRef.current = [];
      setAdminPin(""); setAdminError(""); setDayClose(null); setAdminPinOpen(true);
    }
  };

  const adminVerify = async () => {
    try {
      await api.post(`/public/kiosk/${slug}/verify-exit`, { pin: adminPin });
      setAdminPinOpen(false); setAdminOpen(true); setAdminError("");
    } catch (_) {
      setAdminError(lang === "es" ? "Clave incorrecta" : "Wrong password");
      setAdminPin("");
    }
  };

  const adminDayClose = async () => {
    setAdminBusy(true);
    try {
      // Cierre del POS Transbank integrado, igual que en el cierre de caja del POS
      let tbk_cierre;
      if (config.tbk_enabled) {
        tbk_cierre = {};
        try {
          const tt = await tbkTotales();
          tbk_cierre = { totales_ok: !!tt.aprobado, cantidad: tt.cantidad, total: tt.total };
          const r = await tbkCierre();
          tbk_cierre = { ...tbk_cierre, cierre_ok: !!r.aprobado, mensaje: r.mensaje };
        } catch (e) { tbk_cierre = { ...tbk_cierre, cierre_ok: false, mensaje: e?.message || "El equipo Transbank no respondió" }; }
        if (!tbk_cierre.cierre_ok) setAviso({ tono: "warn", titulo: "Cierre Transbank no realizado", mensaje: `${tbk_cierre.mensaje || ""}\nEl cierre de día del autoservicio se registró igual. Repite el cierre Transbank desde el equipo.` });
      }
      const { data } = await api.post(`/public/kiosk/${slug}/day-close`, { pin: adminPin, tbk_cierre });
      setDayClose(data);
      const tc = data.tbk_cierre;
      const body = [
        `<div class="c xl">${config.company_name || ""}</div>`,
        `<div class="c sm">${config.store_name || ""} · ${config.nombre || ""}</div>`,
        `<div class="sep"></div><div class="c b">CIERRE DE DÍA · AUTOSERVICIO</div>`,
        `<div class="c sm">${data.fecha}</div><div class="sep"></div>`,
        `<div>Ventas: <b>${data.ventas}</b></div>`,
        `<div>Anuladas: ${data.anuladas}</div>`,
        data.primera ? `<div class="sm">Boletas ${data.primera} a ${data.ultima}</div>` : "",
        `<div class="sep"></div>`,
        ...data.por_medio.map((m) => `<div>${m.medio}: $${Number(m.monto).toLocaleString("es-CL")}</div>`),
        `<div class="sep"></div>`,
        `<div class="xxl">TOTAL $${Number(data.total).toLocaleString("es-CL")}</div>`,
        `<div class="sm">IVA $${Number(data.iva).toLocaleString("es-CL")}</div>`,
        tc ? `<div class="sep"></div><div class="c b">CIERRE TRANSBANK</div><div>Estado: <b>${tc.cierre_ok ? "REALIZADO" : "NO REALIZADO"}</b></div>`
          + (tc.mensaje ? `<div class="sm">${String(tc.mensaje).replace(/[<>&"]/g, "")}</div>` : "")
          + (tc.totales_ok ? `<div>Equipo: ${tc.cantidad_pos} venta(s) · $${Number(tc.total_pos || 0).toLocaleString("es-CL")}</div>` : "")
          + `<div>Sistema: ${tc.cantidad_sistema} venta(s) · $${Number(tc.total_sistema || 0).toLocaleString("es-CL")}</div>` : "",
        operator?.nombre ? `<div class="sm">Operador: ${operator.nombre}</div>` : "",
        `<div class="sep"></div><div class="c sm">Ai-POS · Autoservicio</div>`,
      ].join("");
      if (config.agent_enabled) {
        const png = await renderTicketPng(body, "Cierre de día");
        if (png) await agentPrint(config.agent_port, config.agent_token, [{ type: "raster", data_base64: png }], {});
      } else {
        await printHtml(body, "Cierre de día");
      }
    } catch (e) {
      setAviso({ tono: "error", titulo: "Cierre de día", mensaje: e?.response?.data?.detail || "No se pudo cerrar el día" });
    }
    setAdminBusy(false);
  };

  const adminReprint = async () => {
    setAdminBusy(true);
    try {
      const { data } = await api.get(`/public/kiosk/${slug}/last-sale`, { params: { pin: adminPin } });
      await printSale(data.sale);
    } catch (e) {
      setAviso({ tono: "warn", titulo: "Reimprimir", mensaje: e?.response?.data?.detail || "No hay boleta para reimprimir" });
    }
    setAdminBusy(false);
  };

  const adminPower = async (action) => {
    if (!config.agent_enabled) {
      setAviso({ tono: "warn", titulo: lang === "es" ? "Agente no instalado" : "Agent not installed", mensaje: lang === "es"
        ? "Apagar o reiniciar requiere el agente de impresión Ai-POS instalado en este equipo."
        : "Shutdown/restart requires the Ai-POS local agent installed on this machine." });
      return;
    }
    const ok = window.confirm(action === "shutdown"
      ? (lang === "es" ? "¿Apagar el equipo?" : "Shut down the machine?")
      : (lang === "es" ? "¿Reiniciar el equipo?" : "Restart the machine?"));
    if (!ok) return;
    setAdminBusy(true);
    try {
      await agentSystem(config.agent_port, config.agent_token, action);
      reportEvent(action === "shutdown" ? "APAGADO" : "REINICIO", { by: operator?.nombre || null });
    } catch (_) {
      setAviso({ tono: "error", titulo: lang === "es" ? "Agente sin respuesta" : "Agent not responding", mensaje: lang === "es"
        ? "No se pudo contactar al agente. Verifica que esté corriendo en este equipo."
        : "Could not reach the agent. Make sure it is running." });
    }
    setAdminBusy(false);
  };

  const changeOperator = () => {
    localStorage.removeItem(`kiosk_op_${slug}`);
    setOperator(null); setAdminOpen(false); setStarted(false);
  };

  if (!operator) {
    return <KioskOperatorLogin config={config} lang={lang} api={api} slug={slug}
      onReady={(op) => { localStorage.setItem(`kiosk_op_${slug}`, JSON.stringify(op)); setOperator(op); }} />;
  }

  return (
    <>
    {!started && <KioskWelcome config={config} lang={lang} onStart={() => { startedAtRef.current = Date.now(); setStarted(true); }} />}
    {homeAsk && (
      <div className="fixed inset-0 z-[80] bg-slate-950/80 backdrop-blur-sm grid place-items-center p-6" data-testid="kiosk-home-confirm">
        <div className="w-full max-w-sm rounded-3xl bg-slate-900 border border-white/10 p-6 text-center space-y-4">
          <Home className="w-10 h-10 text-sky-400 mx-auto" />
          <p className="text-lg font-bold text-white">{lang === "es" ? "¿Volver al inicio?" : "Go back to start?"}</p>
          <p className="text-sm text-slate-400">{lang === "es" ? `Se perderán los ${cart.length} producto(s) de tu carro.` : `Your ${cart.length} item(s) will be removed.`}</p>
          <div className="grid grid-cols-2 gap-3">
            <button onClick={() => setHomeAsk(false)} data-testid="kiosk-home-cancel" className="h-12 rounded-2xl border border-white/15 text-slate-200 font-semibold">{lang === "es" ? "Seguir comprando" : "Keep shopping"}</button>
            <button onClick={() => irInicio(true)} data-testid="kiosk-home-ok" className="h-12 rounded-2xl bg-rose-500 hover:bg-rose-400 text-white font-bold">{lang === "es" ? "Sí, volver" : "Yes, go back"}</button>
          </div>
        </div>
      </div>
    )}
    {modsPick && (
      <ElegirModificadores product={modsPick.p} onCancel={() => setModsPick(null)}
        onConfirm={(res) => { const m = modsPick; setModsPick(null); addProduct(m.p, m.qty, res); }} />
    )}
    <div className="h-screen overflow-hidden bg-slate-950 text-white flex flex-col" data-testid="kiosk-shop" style={{ display: started ? undefined : "none" }}>
      {/* Zona secreta: 5 toques abren el menú de administración */}
      <button type="button" data-testid="kiosk-admin-corner" onClick={onCornerTap}
        aria-label="admin" className="fixed top-0 left-0 w-24 h-24 z-[75] opacity-0" />
      {printerAlert && (
        <div data-testid="kiosk-printer-alert"
          className="shrink-0 bg-rose-600/90 text-white text-center py-2 px-4 text-sm font-bold print:hidden">
          <AlertTriangle className="w-4 h-4 inline mr-1.5 -mt-0.5" />
          {printerAlert.tipo === "SIN_PAPEL"
            ? (lang === "es" ? "Impresora sin papel — avisa al encargado" : "Printer out of paper — call staff")
            : (lang === "es" ? `Problema con la impresora: ${printerAlert.msg}` : `Printer problem: ${printerAlert.msg}`)}
        </div>
      )}
      <header className="p-4 sm:p-6 flex items-center gap-4 border-b border-white/5 print:hidden">
        <div className="w-12 h-12 sm:w-14 sm:h-14 grid place-items-center">
          <img src="/brand/aipos-logo.png" alt="Ai-POS" className="w-9 h-9 sm:w-11 sm:h-11" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-widest text-sky-300 font-bold">Autoservicio</p>
          <h1 className="text-lg sm:text-2xl font-bold font-display truncate">{config.company_name}</h1>
          <p className="text-xs text-slate-400 mt-0.5 truncate">{config.store_name} · {config.nombre}</p>
        </div>
        <div className="hidden sm:block text-right text-[10px] uppercase tracking-widest text-slate-500">
          <p>Docs: <strong className="text-slate-300">{config.ticket_mode}</strong></p>
          <p>Pago: <strong className="text-slate-300">{config.payment_machine || "—"}</strong></p>
        </div>
        {/* Idioma + voz + salir */}
        <div className="flex items-center gap-1.5 relative">
          {audioPermitido && (
          <button
            data-testid="kiosk-voice-toggle"
            type="button"
            onClick={() => {
              const next = !voiceOn;
              setVoiceOn(next);
              if (next) setTimeout(() => speak(lang === "es" ? "Audio activado" : "Audio on"), 60);
            }}
            className={`h-11 px-3 grid place-items-center rounded-xl border transition ${voiceOn
              ? "bg-emerald-500/15 border-emerald-400/30 text-emerald-300 hover:bg-emerald-500/25"
              : "bg-white/5 border-white/10 text-slate-400 hover:bg-white/10"}`}
            title={lang === "es"
              ? (voiceOn ? "Apagar audio del kiosco" : "Encender audio del kiosco")
              : (voiceOn ? "Turn kiosk audio off" : "Turn kiosk audio on")}
          >
            {voiceOn ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
          </button>
          )}
          {audioPermitido && (
          <button
            data-testid="kiosk-voice-picker-btn"
            onClick={() => setVoiceOpen((v) => !v)}
            className="h-11 px-3 grid place-items-center rounded-xl border bg-white/5 border-white/10 text-slate-300 hover:bg-white/10 transition"
            title={lang === "es" ? "Elegir tipo de voz" : "Choose voice"}
          >
            <Mic className="w-5 h-5" />
          </button>
          )}
          {voiceOpen && (
            <div data-testid="kiosk-voice-menu" className="absolute right-0 top-11 z-50 w-64 rounded-2xl bg-slate-900 border border-white/10 shadow-2xl p-2 max-h-72 overflow-auto">
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 px-2 py-1.5">
                {lang === "es" ? "Voz del sistema" : "System voice"}
              </p>
              {voices.length === 0 ? (
                <p className="text-xs text-slate-500 p-3">{lang === "es" ? "Sin voces disponibles" : "No voices available"}</p>
              ) : (
                <>
                  {[...voices]
                    .sort((a, b) => {
                      const p = lang === "es" ? "es" : "en";
                      const ap = (a.lang || "").toLowerCase().startsWith(p) ? 0 : 1;
                      const bp = (b.lang || "").toLowerCase().startsWith(p) ? 0 : 1;
                      return ap - bp || (a.name || "").localeCompare(b.name || "");
                    })
                    .map((v) => (
                      <button
                        key={v.voiceURI}
                        data-testid={`kiosk-voice-opt-${v.voiceURI.replace(/[^a-z0-9]/gi, "-").slice(0, 40)}`}
                        onClick={() => { setVoiceURI(v.voiceURI); setVoiceOpen(false); setTimeout(() => speak(lang === "es" ? "Voz seleccionada" : "Voice selected"), 100); }}
                        className={`w-full text-left px-2 py-2 rounded-lg text-xs flex items-center gap-2 ${voiceURI === v.voiceURI ? "bg-sky-500/25 text-white" : "hover:bg-white/5 text-slate-200"}`}
                      >
                        <span className="text-[10px] font-mono bg-white/10 rounded px-1 py-0.5 uppercase">{v.lang}</span>
                        <span className="truncate flex-1">{v.name}</span>
                        {voiceURI === v.voiceURI && <CheckCircle2 className="w-3.5 h-3.5 text-sky-300" />}
                      </button>
                    ))}
                </>
              )}
            </div>
          )}
          <button
            data-testid="kiosk-home-btn"
            onClick={() => irInicio()}
            className="h-9 px-3 rounded-xl bg-white/10 hover:bg-white/20 text-white text-xs font-bold border border-white/10 flex items-center gap-1.5"
          >
            <Home className="w-4 h-4" /> {lang === "es" ? "Inicio" : "Home"}
          </button>
          <button
            data-testid="kiosk-lang-toggle"
            onClick={() => setLang(lang === "es" ? "en" : "es")}
            className="h-9 px-3 rounded-xl bg-white/10 hover:bg-white/20 text-white text-xs font-bold border border-white/10"
          >
            {lang === "es" ? "🇺🇸 EN" : "🇨🇱 ES"}
          </button>
          <button
            data-testid="kiosk-exit-btn"
            onClick={() => { setExitOpen(true); setExitPin(""); setExitError(""); }}
            className="h-9 px-3 rounded-xl bg-rose-500/20 hover:bg-rose-500/30 text-rose-200 text-xs font-bold border border-rose-500/40"
            title={lang === "es" ? "Salir del modo autoservicio" : "Exit self-service mode"}
          >
            <Lock className="w-3.5 h-3.5" />
          </button>
        </div>
      </header>

      <div className="flex-1 min-h-0 flex flex-col lg:flex-row overflow-hidden print:hidden">
        {/* Catálogo (scroll propio) + aviso del lector fijo al pie */}
        <div className="flex-1 min-w-0 min-h-0 flex flex-col">
        <main ref={gridScrollRef} className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-6">
          <div className="text-center mb-6">
            <h2 className="text-3xl sm:text-5xl font-extrabold font-display leading-tight">{t.heading}</h2>
            <p className="mt-2 text-slate-400 text-base sm:text-lg">{t.sub}</p>
          </div>
          {/* Promociones */}
          {promoVer && (
            <div data-testid="kiosk-promo-filtro" className="sticky top-2 z-20 backdrop-blur mb-4 flex items-center justify-between gap-3 rounded-2xl bg-amber-500/15 border border-amber-400/30 px-4 py-3">
              <p className="text-sm text-amber-100"><b>{promoVer.nombre}</b> · {lang === "es" ? "elige tus productos, el precio se aplica en el carro" : "pick your items, price applies in cart"}</p>
              <button onClick={() => setPromoVer(null)} data-testid="kiosk-promo-filtro-quitar" className="shrink-0 text-xs font-bold text-amber-200 hover:text-white">{lang === "es" ? "Ver todo" : "See all"}</button>
            </div>
          )}
          {promosKiosk.length > 0 && (
            <div data-testid="kiosk-promotions-strip" className="mb-5">
              <p className="text-xs font-bold uppercase tracking-widest text-amber-300 mb-2">🎉 {t.promotions}</p>
              <div className="flex gap-3 overflow-x-auto pb-2">
                {promosKiosk.map((pr) => {
                  const regular = !esCombo(pr) ? 0 : (pr.items || []).reduce((s, pi) => {
                    const prod = productById.get(pi.product_id);
                    return s + ((prod?.precio || 0) * pi.cantidad);
                  }, 0);
                  const saving = Math.max(0, regular - (pr.precio_bundle || 0));
                  const unidades = esCombo(pr) ? (pr.items || []).reduce((s2, pi) => s2 + Number(pi.cantidad || 0), 0)
                    : pr.tipo === "PACK_COMBINABLE" ? Number(pr.cantidad_pack || 0) : 0;
                  return (
                    <button
                      key={pr.promotion_id}
                      data-testid={`kiosk-promo-${pr.promotion_id}`}
                      onClick={() => applyPromo(pr)}
                      className="shrink-0 text-left rounded-3xl bg-gradient-to-br from-amber-500 to-orange-600 text-white overflow-hidden min-w-[220px] w-[220px] hover:from-amber-400 hover:to-orange-500 active:scale-95 transition shadow-lg shadow-orange-500/20"
                    >
                      {pr.image_url && (
                        <img src={resolveMedia(pr.image_url)} alt={pr.nombre} loading="lazy"
                          data-testid={`kiosk-promo-img-${pr.promotion_id}`}
                          className="w-full h-28 object-cover" />
                      )}
                      <div className="p-4">
                        <p className="text-sm font-bold leading-tight line-clamp-2 min-h-[2.5rem]">{pr.nombre}</p>
                        <div className="flex flex-wrap gap-1.5 mt-2 min-h-[1.375rem]">
                          {unidades > 0 && (
                            <span data-testid={`kiosk-promo-unidades-${pr.promotion_id}`} className="text-[11px] bg-slate-900/35 rounded-full px-2 py-0.5 font-bold">
                              {pr.tipo === "PACK_COMBINABLE" ? (lang === "es" ? `Elige ${unidades} unidades` : `Pick ${unidades} units`) : (lang === "es" ? `${unidades} unidades` : `${unidades} units`)}
                            </span>)}
                          {saving > 0 && (
                            <span data-testid={`kiosk-promo-ahorro-${pr.promotion_id}`} className="text-[11px] bg-white text-orange-700 rounded-full px-2 py-0.5 font-extrabold">
                              {lang === "es" ? "Ahorras" : "Save"} {clp(saving)}
                            </span>)}
                        </div>
                        <span className="block mt-2 text-2xl font-extrabold font-mono-num leading-none">{clp(pr.precio_bundle)}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          {/* Buscador manual (se activa por terminal) */}
          {config?.kiosk_buscador !== false && (
            <button data-testid="kiosk-search-btn" onClick={() => { setBuscarOpen(true); setBuscarQ(""); }}
              className="mb-4 w-full h-16 rounded-2xl bg-amber-400 hover:bg-amber-300 text-slate-900 text-lg font-extrabold flex items-center justify-center gap-3 shadow-lg shadow-amber-400/30 ring-2 ring-amber-200/60 transition">
              <SearchIcon className="w-6 h-6" /> {lang === "es" ? "BUSCAR UN PRODUCTO POR NOMBRE" : "SEARCH A PRODUCT BY NAME"}
            </button>
          )}
          {/* Categorías */}
          {categories.length > 0 && (
            <div className="flex gap-2 overflow-x-auto pb-3 mb-4">
              <button data-testid="kiosk-cat-all" onClick={() => { setCat("ALL"); setPromoVer(null); }}
                className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap ${cat === "ALL" ? "bg-sky-500 text-white" : "bg-white/10 text-slate-300 hover:bg-white/15"}`}>
                Todos
              </button>
              {categories.map((c) => (
                <button key={c.category_id} data-testid={`kiosk-cat-${c.nombre}`} onClick={() => { setCat(c.category_id); setPromoVer(null); }}
                  className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap ${cat === c.category_id ? "bg-sky-500 text-white" : "bg-white/10 text-slate-300 hover:bg-white/15"}`}>
                  {c.nombre}
                </button>
              ))}
            </div>
          )}

          {filtered.length === 0 ? (
            <p className="text-center text-slate-500 py-20">Sin productos publicados para autoservicio.</p>
          ) : (
            <>
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
              {visibles.map((p) => (
                <ProductCard key={p.product_id} p={p} onPick={pickProduct} />
              ))}
            </div>
            {visibles.length < filtered.length && (
              <div ref={sentinelRef} data-testid="kiosk-grid-sentinel" data-visible={visibleCount}
                className="py-8 flex flex-col items-center gap-3">
                <p className="text-slate-500 text-sm">
                  {lang === "es"
                    ? `Mostrando ${visibles.length} de ${filtered.length} productos`
                    : `Showing ${visibles.length} of ${filtered.length} products`}
                </p>
                <button data-testid="kiosk-grid-more" onClick={() => setVisibleCount((n) => n + LOTE * 2)}
                  className="h-14 px-8 rounded-2xl bg-white/10 hover:bg-white/20 border border-white/10 text-white font-bold">
                  {lang === "es" ? "Ver más productos" : "Show more products"}
                </button>
              </div>
            )}
            </>
          )}
        </main>
        {/* Aviso del lector: fijo al final de la columna de productos */}
        <div className="shrink-0 px-4 sm:px-6 pb-4">
          {/* §13.5 · Gráfica grande que indica dónde está el lector de códigos */}
          <div data-testid="kiosk-scanner-guide"
            className="flex items-center gap-5 sm:gap-7 rounded-[2rem] border-2 border-sky-400/40 bg-gradient-to-r from-sky-500/20 via-sky-500/10 to-transparent px-5 sm:px-7 py-5 sm:py-6 shadow-[0_0_40px_-10px_rgba(14,165,233,0.6)]">
            <div className="relative w-28 h-28 sm:w-32 sm:h-32 shrink-0 rounded-3xl bg-slate-900 border-2 border-sky-400/50 grid place-items-center overflow-hidden">
              <div className="scan-beam w-20 h-[6px] rounded-full bg-rose-500 shadow-[0_0_26px_6px_rgba(244,63,94,0.85)]" />
              <div className="absolute bottom-3 flex gap-[3px] items-end">
                {[7, 4, 9, 3, 8, 5, 9, 4, 7, 3].map((h, i) => (
                  <span key={i} className="w-[4px] bg-white/80 rounded-sm" style={{ height: `${h * 3}px` }} />
                ))}
              </div>
            </div>
            <div className="min-w-0">
              <p className="text-2xl sm:text-4xl font-black font-display text-white leading-tight flex items-center gap-3">
                <ScanLine className="w-7 h-7 sm:w-9 sm:h-9 text-sky-300 shrink-0" />
                {lang === "es" ? "Pasa el código de barras por el lector" : "Scan your barcode on the reader"}
              </p>
              <p className="text-base sm:text-xl text-sky-100/80 mt-2">
                {lang === "es"
                  ? "Está justo bajo la pantalla · también puedes tocar el producto en la lista"
                  : "It is right below the screen · you can also tap the product"}
              </p>
            </div>
            <div className="ml-auto hidden sm:flex flex-col items-center text-sky-300">
              <span className="text-sm font-black uppercase tracking-widest">{lang === "es" ? "Aquí" : "Here"}</span>
              <svg className="w-12 h-12 animate-bounce" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M12 5v14M5 12l7 7 7-7" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
          </div>
        </div>
        </div>

        {/* Carrito lateral */}
        <aside data-testid="kiosk-cart" className="w-full lg:w-96 shrink-0 bg-slate-900/60 border-t lg:border-t-0 lg:border-l border-white/5 flex flex-col min-h-0 max-h-[45vh] lg:max-h-none lg:h-full">
          <div className="p-4 border-b border-white/5 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ShoppingBag ref={cartAnchorRef} className="w-5 h-5 text-sky-400" />
              <div>
                <h3 className="font-bold text-white">{t.my_order}</h3>
                <p className="text-xs text-slate-400">{cart.length} {lang === "es" ? (cart.length !== 1 ? "ítems" : "ítem") : (cart.length !== 1 ? "items" : "item")}</p>
              </div>
            </div>
            {cart.length > 0 && (
              <button data-testid="kiosk-cart-clear" onClick={() => setClearAskOpen(true)} className="text-xs text-slate-400 hover:text-rose-300 font-semibold flex items-center gap-1">
                <X className="w-3.5 h-3.5" /> {t.clear}
              </button>
            )}
          </div>
          <div data-testid="kiosk-cart-items" className="flex-1 min-h-0 overflow-y-auto p-3 space-y-2">
            {cart.length === 0 ? (
              <div className="text-center py-12 text-slate-500 text-sm">
                <ShoppingBag className="w-12 h-12 mx-auto mb-3 opacity-40" />
                {t.empty_cart}
              </div>
            ) : cart.map((it) => (
              <div key={lineKey(it)} data-testid={`kiosk-cart-item-${it.mods_sel ? lineKey(it) : it.sku}`} className="bg-white/5 rounded-2xl p-3">
                <div className="flex justify-between items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-white line-clamp-2">{it.nombre}</p>
                    {it.modificadores?.length > 0 && <p data-testid={`kiosk-cart-mods-${lineKey(it)}`} className="text-xs text-sky-300 font-semibold">{it.modificadores.map((m) => m.nombre).join(" · ")}</p>}
                  </div>
                  <button data-testid={`kiosk-cart-remove-${it.sku}`}
                    onClick={() => { rm(lineKey(it)); speak(t.v_removed(it.nombre)); }}
                    className="w-12 h-12 -mt-1 -mr-1 shrink-0 rounded-xl grid place-items-center text-slate-300 hover:text-rose-300 hover:bg-white/10 active:scale-95 transition">
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
                <div className="flex items-center justify-between mt-2">
                  <div className="flex items-center gap-2">
                    <button data-testid={`kiosk-cart-minus-${it.sku}`} onClick={() => inc(lineKey(it), -1)}
                      className="w-12 h-12 rounded-xl bg-white/10 hover:bg-white/20 active:scale-95 transition grid place-items-center">
                      <Minus className="w-5 h-5" />
                    </button>
                    <span className="w-10 text-center text-lg font-mono-num font-bold">{it.cantidad}</span>
                    <button data-testid={`kiosk-cart-plus-${it.sku}`} onClick={() => inc(lineKey(it), 1)}
                      className="w-12 h-12 rounded-xl bg-white/10 hover:bg-white/20 active:scale-95 transition grid place-items-center">
                      <Plus className="w-5 h-5" />
                    </button>
                  </div>
                  <span className="text-base font-bold font-mono-num text-sky-200">{clp(it.precio * it.cantidad)}</span>
                </div>
              </div>
            ))}
          </div>
          <div className="shrink-0 p-4 border-t border-white/5 space-y-1.5 text-sm bg-slate-900/90">
            {cart.length > 0 && (
              <div className="flex gap-2 pb-2">
                <button data-testid="kiosk-coupon-open" onClick={() => { setRutInput(""); setCuponOpen(true); }}
                  className={`flex-1 h-10 rounded-xl text-xs font-bold border transition ${cuponOk
                    ? "bg-emerald-500/20 border-emerald-400/40 text-emerald-200"
                    : "bg-white/5 border-white/10 text-slate-300 hover:bg-white/10"}`}>
                  <Ticket className="w-4 h-4 inline mr-1" />
                  {cuponOk ? `${t.coupon} ${quote.cupon.code}` : t.coupon_cta}
                </button>
                <button data-testid="kiosk-member-open" onClick={() => { setRutInput(socio?.rut || ""); setSocioOpen(true); }}
                  className={`flex-1 h-10 rounded-xl text-xs font-bold border transition ${socio?.valido
                    ? "bg-amber-400/20 border-amber-300/40 text-amber-200"
                    : "bg-white/5 border-white/10 text-slate-300 hover:bg-white/10"}`}>
                  <Gift className="w-4 h-4 inline mr-1" />
                  {socio?.valido ? socio.rut : t.member_cta}
                </button>
              </div>
            )}
            {(quote?.ahorro_total > 0 || promoDiscount > 0) && (
              <div className="flex justify-between text-slate-400"><span>{t.subtotal}</span>
                <span className="font-mono-num">{clp(quote ? quote.subtotal_lista : rawTotal)}</span></div>
            )}
            {quote?.ahorro_cantidad > 0 && (
              <div data-testid="kiosk-qty-discount" className="flex justify-between text-sky-300">
                <span>{t.quantity_saving}</span><span className="font-mono-num">-{clp(quote.ahorro_cantidad)}</span>
              </div>
            )}
            {(quote?.promos || []).map((pr, i) => (
              <div key={i} data-testid={`kiosk-promo-line-${i}`} className="flex justify-between text-emerald-300">
                <span className="truncate pr-2">🎉 {pr.nombre}</span>
                <span className="font-mono-num shrink-0">-{clp(pr.ahorro)}</span>
              </div>
            ))}
            {!quote && promoDiscount > 0 && (
              <div data-testid="kiosk-promo-discount" className="flex justify-between text-emerald-300">
                <span>🎉 {t.promo_discount}</span><span className="font-mono-num">-{clp(promoDiscount)}</span>
              </div>
            )}
            {cuponOk && (
              <div data-testid="kiosk-coupon-line" className="flex justify-between text-amber-300">
                <span className="truncate pr-2">{t.coupon} {quote.cupon.code}</span>
                <span className="font-mono-num shrink-0">-{clp(quote.cupon.descuento)}</span>
              </div>
            )}
            <div className="flex justify-between text-slate-400"><span>{t.iva_included}</span><span className="font-mono-num">{clp(iva)}</span></div>
            <div className="flex justify-between items-end pt-2 border-t border-white/5">
              <span className="text-slate-300 font-semibold">{t.total}</span>
              <span data-testid="kiosk-cart-total" className="text-3xl font-extrabold text-white font-mono-num">{clp(total)}</span>
            </div>
            {quote?.loyalty?.habilitado && socio?.valido && (
              <div data-testid="kiosk-loyalty-line" className="flex justify-between text-amber-200 text-xs pt-1">
                <span>{t.member_earn}</span>
                <span className="font-mono-num font-bold">+{clp(quote.loyalty.por_ganar)}</span>
              </div>
            )}
          </div>
          <div className="shrink-0 p-4 pt-0 bg-slate-900/90">
            <button
              data-testid="kiosk-pay-btn"
              disabled={!cart.length}
              onClick={() => {
                // Alcohol, cigarrillos: primero hay que acreditar la edad
                if (config?.pedir_autorizacion_edad !== false && itemsRestringidos.length > 0 && !ageAuth) {
                  setAgeMode("CEDULA"); setAgePin(""); setAgeError(""); setAgeOpen(true);
                  speak(lang === "es"
                    ? "Se requiere autorización. Escanea tu cédula de identidad o llama a un supervisor."
                    : "Authorization required. Please scan your ID or call a supervisor.");
                  return;
                }
                abrirPago();
              }}
              className="w-full h-16 rounded-2xl bg-sky-500 hover:bg-sky-400 disabled:bg-slate-700 disabled:text-slate-500 text-white text-lg font-extrabold shadow-lg shadow-sky-500/20 transition flex items-center justify-center gap-2"
            >
              <CreditCard className="w-6 h-6" /> {t.pay} {cart.length > 0 ? clp(total) : ""}
            </button>
          </div>
        </aside>
      </div>

      <footer className="shrink-0 py-1.5 border-t border-white/5 text-center text-[10px] text-slate-500 uppercase tracking-widest print:hidden">
        {operator?.nombre && <span className="text-emerald-300 mr-2">{lang === "es" ? "Operador" : "Operator"}: {operator.nombre}</span>}
        {lang === "es" ? "Impresora" : "Printer"}: {config.printer_mode || config.printer_type} · terminal {config.terminal_id.slice(-8)}
        {started && <span data-testid="kiosk-idle-countdown" className="ml-2 text-slate-400">· {lang === "es" ? "vuelve al inicio en" : "back to start in"} {idleLeft}s</span>}
      </footer>

      {/* Modal Pago */}
      {payOpen && (
        <div className="fixed inset-0 bg-slate-950/90 backdrop-blur-sm z-50 grid place-items-center p-4 print:hidden" data-testid="kiosk-pay-modal">
          <div className="bg-slate-900 border border-white/10 rounded-3xl p-6 sm:p-8 max-w-md w-full">
            <div className="text-center">
              <div className="mx-auto w-20 h-20 rounded-3xl bg-sky-500/20 grid place-items-center mb-4">
                <CreditCard className="w-10 h-10 text-sky-300" />
              </div>
              <p className="text-sm uppercase tracking-widest text-slate-400">{t.total_to_pay}</p>
              <p className="text-5xl font-extrabold font-mono-num text-white mt-2">{clp(total)}</p>
              {!tp.open && (metodos.length < 2 || processing) && <><p className="mt-6 text-slate-300 text-sm">
                {t.bring_card} <strong className="text-white">{(config.payment_machine && config.payment_machine !== "NONE") ? config.payment_machine : (lang === "es" ? "de la caja" : "at the counter")}</strong>.
              </p>
              <div
                data-testid="kiosk-pay-right-notice"
                className="mt-4 flex items-center justify-center gap-3 rounded-2xl bg-amber-400/10 border border-amber-300/30 px-4 py-3"
              >
                <ArrowRight className="w-6 h-6 text-amber-300 shrink-0 animate-pulse" />
                <p className="text-sm font-bold text-amber-200 text-left">{t.pay_right}</p>
              </div>
              {!config.tbk_enabled && !config.c2c_enabled && <p className="mt-1 text-xs text-slate-500">{t.sim_note}</p>}
              {tbkEstado && <p data-testid="kiosk-tbk-estado" className="mt-3 text-sm font-semibold text-sky-300 animate-pulse">{tbkEstado}</p>}
              {tbkEstado && config.c2c_enabled && !config.tbk_enabled && (
                <button data-testid="kiosk-c2c-cancelar" onClick={() => { c2cCancelRef.current = true; }} className="mt-3 text-sm font-bold text-rose-300 hover:underline">
                  {lang === "es" ? "Cancelar pago" : "Cancel payment"}
                </button>
              )}</>}
            </div>
            {metodos.length > 1 && !tp.open && !processing && (
              <div className="mt-6 space-y-3" data-testid="kiosk-pay-methods">
                <p className="text-center text-sm font-bold uppercase tracking-widest text-slate-400">
                  {lang === "es" ? "Elige tu forma de pago" : "Choose how to pay"}
                </p>
                <button data-testid="kiosk-pay-method-tbk" onClick={() => { speak(lang === "es" ? "Opera tu tarjeta en el terminal de pago" : "Use your card on the payment terminal"); checkout(); }}
                        className="w-full h-20 rounded-2xl bg-sky-500 hover:bg-sky-400 active:scale-[0.98] transition-transform text-white text-xl font-extrabold flex items-center justify-center gap-3 shadow-lg shadow-sky-500/20">
                  <CreditCard className="w-7 h-7" /> {lang === "es" ? "Tarjeta débito / crédito" : "Debit / credit card"}
                </button>
                <button data-testid="kiosk-tp-open" onClick={() => setTp({ ...TP0, open: true })}
                        className="w-full h-20 rounded-2xl bg-violet-500 hover:bg-violet-400 active:scale-[0.98] transition-transform text-white text-xl font-extrabold flex items-center justify-center gap-3 shadow-lg shadow-violet-500/20">
                  <Ticket className="w-7 h-7" /> {config.tarjeta_propia}
                </button>
              </div>
            )}
            {processing && !tp.open && metodos.includes("TBK") && (
              <div data-testid="kiosk-tbk-waiting" className="mt-6 flex items-center justify-center gap-3 text-sky-200">
                <Loader2 className="w-6 h-6 animate-spin" />
                <span className="font-bold">{lang === "es" ? "Procesando pago en el terminal…" : "Processing on terminal…"}</span>
              </div>
            )}
            {tp.open && (
              <div className="mt-5 space-y-3 rounded-2xl border border-violet-300/30 bg-violet-500/10 p-4" data-testid="kiosk-tp-panel">
                <p className="text-sm font-bold text-violet-100">{config.tarjeta_propia}</p>
                {!/^\d{20}$/.test(tp.codigo.replace(/\s/g, "")) ? (
                  <input autoFocus value={tp.codigo} data-testid="kiosk-tp-codigo"
                         onChange={(e) => {
                           const v = e.target.value;
                           const listo = /^\d{20}$/.test(v.replace(/\s/g, ""));
                           setTp({ ...tp, codigo: v, err: null, ...(listo ? { pin: "", keys: teclasAzar() } : {}) });
                           if (listo) {
                             e.target.blur();
                             speak(lang === "es" ? "Ingresa tu clave. Por tu seguridad, los números del teclado cambian de lugar en cada pago." : "Enter your PIN. For your security, the keypad numbers change position on every payment.");
                           }
                         }}
                         onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.stopPropagation(); } }}
                         placeholder={lang === "es" ? "Escanea el QR de tu tarjeta" : "Scan your card QR"}
                         className="w-full h-12 rounded-xl bg-slate-950/60 border border-white/10 px-3 text-white placeholder:text-slate-500 font-mono-num" />
                ) : (
                  <div data-testid="kiosk-tp-pinpad" className="space-y-3">
                    <div className="flex items-center justify-between text-xs text-violet-200">
                      <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-emerald-300" /> {lang === "es" ? "Tarjeta leída" : "Card read"} ···{tp.codigo.replace(/\s/g, "").slice(-4)}</span>
                      <button data-testid="kiosk-tp-cambiar" onClick={() => setTp({ ...TP0, open: true })} disabled={tp.busy} className="underline underline-offset-2">{lang === "es" ? "Leer otra" : "Rescan"}</button>
                    </div>
                    <p className="text-center text-sm font-bold text-white">{lang === "es" ? "Ingresa tu clave" : "Enter your PIN"}</p>
                    <p data-testid="kiosk-tp-aviso-teclado" className="flex items-center justify-center gap-1.5 text-xs text-amber-200 bg-amber-400/10 border border-amber-300/20 rounded-xl px-3 py-2">
                      <ShieldAlert className="w-4 h-4 shrink-0" />
                      {lang === "es" ? "Por tu seguridad, los números cambian de lugar en cada pago" : "For your security, numbers change position on every payment"}
                    </p>
                    <div data-testid="kiosk-tp-pin" className="flex justify-center gap-4 py-1">
                      {[0, 1, 2, 3].map((i) => (
                        <span key={i} className={`w-5 h-5 rounded-full border-2 transition-colors ${i < tp.pin.length ? "bg-violet-300 border-violet-300" : "border-violet-300/40"}`} />
                      ))}
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      {(tp.keys || teclasAzar()).map((k) => (
                        k === "ok" ? (
                          <button key={k} onClick={pagarTP} disabled={tp.busy || tp.pin.length < 4} data-testid="kiosk-tp-pagar"
                                  className="h-16 rounded-2xl bg-violet-500 hover:bg-violet-400 disabled:opacity-40 text-white text-lg font-extrabold flex items-center justify-center">
                            {tp.busy ? <Loader2 className="w-6 h-6 animate-spin" /> : (lang === "es" ? "Pagar" : "Pay")}
                          </button>
                        ) : (
                          <button key={k} data-testid={`kiosk-tp-key-${k}`} disabled={tp.busy}
                                  onClick={() => setTp((p) => ({ ...p, err: null, pin: k === "del" ? p.pin.slice(0, -1) : (p.pin + k).slice(0, 4) }))}
                                  className={`h-16 rounded-2xl text-2xl font-black active:scale-95 transition-transform ${k === "del" ? "bg-white/5 text-slate-300 text-base" : "bg-slate-950/60 border border-white/10 text-white hover:bg-white/10"}`}>
                            {k === "del" ? (lang === "es" ? "Borrar" : "Delete") : k}
                          </button>
                        )
                      ))}
                    </div>
                  </div>
                )}
                {tp.err && <p className="text-sm font-semibold text-rose-300" data-testid="kiosk-tp-error">{tp.err}</p>}
                <button data-testid="kiosk-tp-cancel" onClick={() => { setTp(TP0); if (metodos.length < 2) setPayOpen(false); }} disabled={tp.busy} className="w-full h-12 rounded-xl border border-white/10 text-slate-300 font-semibold">{t.cancel}</button>
              </div>
            )}
            {metodos.length > 1 && !tp.open && !processing && (
              <button data-testid="kiosk-pay-back" onClick={() => setPayOpen(false)}
                      className="mt-3 w-full h-12 rounded-2xl border border-white/10 hover:bg-white/5 text-slate-300 font-semibold">{t.cancel}</button>
            )}
            <div className={`mt-6 grid grid-cols-2 gap-3 ${tp.open || metodos.length ? "hidden" : ""}`}>
              <button
                onClick={() => { setPayOpen(false); setTp(TP0); }}
                disabled={processing}
                className="h-14 rounded-2xl border border-white/10 hover:bg-white/5 text-slate-300 font-semibold"
              >
                {t.cancel}
              </button>
              <button
                data-testid="kiosk-pay-confirm"
                onClick={() => checkout()}
                disabled={processing}
                className="h-14 rounded-2xl bg-emerald-500 hover:bg-emerald-400 text-white font-extrabold flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {processing ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle2 className="w-5 h-5" />}
                {t.confirm}
              </button>
            </div>
          </div>
        </div>
      )}

      {aviso && <KioskAviso aviso={aviso} onClose={() => setAviso(null)} />}

      {/* Producto no encontrado al escanear: se cierra solo a los 5 s o con Aceptar */}
      {scanError && (
        <div className="fixed inset-0 bg-slate-950/90 backdrop-blur-sm z-[75] grid place-items-center p-6" data-testid="kiosk-scan-error-modal">
          <div className="bg-slate-900 border-2 border-amber-400/40 rounded-3xl p-8 max-w-md w-full text-center">
            <div className="mx-auto w-20 h-20 rounded-3xl bg-amber-400/15 grid place-items-center">
              <AlertTriangle className="w-11 h-11 text-amber-300" />
            </div>
            <h3 className="mt-4 text-2xl font-black text-white font-display">{t.not_found_title}</h3>
            <p className="mt-2 text-slate-300">{t.not_found_body}</p>
            <p className="mt-3 font-mono-num text-amber-200 text-lg bg-white/5 rounded-xl py-2" data-testid="kiosk-scan-error-code">
              {scanError.code}
            </p>
            <button data-testid="kiosk-scan-error-accept" onClick={() => setScanError(null)}
              className="mt-6 w-full h-14 rounded-2xl bg-amber-400 hover:bg-amber-300 text-slate-900 font-black text-lg">
              {t.accept}
            </button>
          </div>
        </div>
      )}

      {/* Cupón de descuento */}
      {cuponOpen && (
        <div className="fixed inset-0 bg-slate-950/90 backdrop-blur-sm z-[75] grid place-items-center p-6" data-testid="kiosk-coupon-modal">
          <div className="bg-slate-900 border border-white/10 rounded-3xl p-7 max-w-md w-full">
            <div className="text-center">
              <Ticket className="w-10 h-10 text-emerald-300 mx-auto" />
              <h3 className="mt-3 text-2xl font-black text-white font-display">{t.coupon_title}</h3>
              <p className="text-sm text-slate-400 mt-1">{t.coupon_hint}</p>
            </div>
            <input
              data-testid="kiosk-coupon-input" autoFocus value={cupon}
              onChange={(e) => setCupon(e.target.value.toUpperCase())}
              onKeyDown={(e) => { if (e.key === "Enter") setCuponOpen(false); }}
              className="mt-5 w-full h-16 rounded-2xl bg-white/5 border border-white/10 text-center text-2xl font-mono-num tracking-[0.2em] text-white uppercase focus:outline-none focus:border-emerald-400"
              placeholder="XXXXXX"
            />
            {cupon && quote?.cupon && !quote.cupon.valido && (
              <p data-testid="kiosk-coupon-error" className="mt-2 text-center text-sm text-rose-300">
                {quote.cupon.error || t.coupon_invalid}
              </p>
            )}
            {cuponOk && (
              <p data-testid="kiosk-coupon-ok" className="mt-2 text-center text-sm text-emerald-300 font-bold">
                -{clp(quote.cupon.descuento)} · {quote.cupon.descripcion || quote.cupon.code}
              </p>
            )}
            <div className="mt-6 grid grid-cols-2 gap-3">
              <button onClick={() => { setCupon(""); setCuponOpen(false); }}
                className="h-14 rounded-2xl border border-white/10 text-slate-300 font-semibold">{t.remove}</button>
              <button data-testid="kiosk-coupon-apply" onClick={() => setCuponOpen(false)}
                className="h-14 rounded-2xl bg-emerald-500 hover:bg-emerald-400 text-white font-extrabold">{t.apply}</button>
            </div>
          </div>
        </div>
      )}

      {/* Cliente con puntos (fidelización) */}
      {socioOpen && (
        <div className="fixed inset-0 bg-slate-950/90 backdrop-blur-sm z-[75] grid place-items-center p-6" data-testid="kiosk-member-modal">
          <div className="bg-slate-900 border border-white/10 rounded-3xl p-7 max-w-md w-full">
            <div className="text-center">
              <Gift className="w-10 h-10 text-amber-300 mx-auto" />
              <h3 className="mt-3 text-2xl font-black text-white font-display">{t.member_title}</h3>
            </div>
            <p data-testid="kiosk-member-rut" className="mt-5 h-16 rounded-2xl bg-white/5 border border-white/10 grid place-items-center text-2xl font-mono-num tracking-widest text-white">
              {rutInput || "—"}
            </p>
            <input
              data-testid="kiosk-member-scan-input"
              autoFocus
              value={lectorSocio.valorCrudo}
              onChange={(e) => lectorSocio.alEscribir(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); lectorSocio.alEnter(e.target.value); } }}
              className="mt-2 w-full h-11 rounded-xl bg-white/5 border border-white/10 text-center text-xs font-mono-num text-slate-300 px-3"
              placeholder={lang === "es" ? "o escanea el QR de tu cédula" : "or scan your ID card QR"}
            />
            <div className="mt-4 grid grid-cols-3 gap-2">
              {["1", "2", "3", "4", "5", "6", "7", "8", "9", "K", "0", "⌫"].map((k) => (
                <button key={k} data-testid={`kiosk-member-key-${k}`}
                  onClick={() => setRutInput((v) => k === "⌫" ? v.slice(0, -1) : (v + k).slice(0, 10))}
                  className="h-14 rounded-2xl bg-white/5 hover:bg-white/10 border border-white/10 text-xl font-bold text-white">
                  {k}
                </button>
              ))}
            </div>
            {socio?.valido && (
              <div data-testid="kiosk-member-info" className="mt-4 rounded-2xl bg-amber-400/10 border border-amber-300/30 p-3 text-center">
                {socio.found ? (
                  <>
                    <p className="text-white font-bold">{socio.nombre || socio.rut}</p>
                    <p className="text-sm text-amber-200">{t.member_points}: <b className="font-mono-num">{clp(socio.balance)}</b>{socio.tier ? ` · ${socio.tier}` : ""}</p>
                  </>
                ) : <p className="text-sm text-amber-200">{t.member_not_found}</p>}
              </div>
            )}
            <div className="mt-5 grid grid-cols-2 gap-3">
              <button onClick={() => { setSocio(null); setSocioOpen(false); }}
                className="h-14 rounded-2xl border border-white/10 text-slate-300 font-semibold">{t.remove}</button>
              <button data-testid="kiosk-member-apply" disabled={rutInput.length < 8}
                onClick={async () => {
                  try {
                    const r = await api.get(`/public/kiosk/${slug}/loyalty?rut=${encodeURIComponent(rutInput)}&total=${total}`);
                    if (!r.data?.valido) { setSocio({ valido: false }); return; }
                    setSocio(r.data);
                  } catch (_) { setSocio({ valido: false }); }
                }}
                className="h-14 rounded-2xl bg-amber-400 hover:bg-amber-300 text-slate-900 font-extrabold disabled:opacity-40">{t.apply}</button>
            </div>
            {socio && !socio.valido && (
              <p className="mt-2 text-center text-sm text-rose-300">{lang === "es" ? "RUT inválido" : "Invalid ID"}</p>
            )}
            {socio?.valido && (
              <button data-testid="kiosk-member-close" onClick={() => setSocioOpen(false)}
                className="mt-3 w-full h-12 rounded-2xl bg-white/5 border border-white/10 text-white font-bold">
                {t.continue_}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Buscador manual con teclado en pantalla */}
      {buscarOpen && (
        <div className="fixed inset-0 bg-slate-950/95 backdrop-blur-sm z-[80] p-4 flex flex-col" data-testid="kiosk-search-modal">
          <div className="max-w-3xl w-full mx-auto flex flex-col flex-1 min-h-0">
            <div className="flex items-center gap-3">
              <p data-testid="kiosk-search-value"
                 className="flex-1 h-16 rounded-2xl bg-white/5 border border-white/10 grid place-items-center text-2xl font-bold text-white uppercase">
                {buscarQ || (lang === "es" ? "Escribe el producto" : "Type the product")}
              </p>
              <button data-testid="kiosk-search-close" onClick={() => setBuscarOpen(false)}
                className="h-16 px-5 rounded-2xl bg-white/10 text-white font-bold">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div data-testid="kiosk-search-results" className="mt-4 flex-1 min-h-0 overflow-y-auto grid sm:grid-cols-2 gap-3 content-start">
              {buscarQ.trim().length < 2 ? (
                <p className="sm:col-span-2 text-center text-slate-400 py-6">
                  {lang === "es" ? "Escribe al menos 2 letras" : "Type at least 2 letters"}
                </p>
              ) : resultadosBusqueda.length === 0 ? (
                <p data-testid="kiosk-search-empty" className="sm:col-span-2 text-center text-slate-400 py-6">
                  {lang === "es" ? "Sin resultados" : "No results"}
                </p>
              ) : resultadosBusqueda.map((p) => (
                <button key={p.product_id} data-testid={`kiosk-search-result-${p.product_id}`}
                  onClick={() => { addProduct(p, 1); setBuscarOpen(false); }}
                  className="flex items-center gap-3 p-3 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 text-left">
                  {p.image_url
                    ? <img src={p.image_url} alt="" className="w-14 h-14 rounded-xl object-cover" />
                    : <div className="w-14 h-14 rounded-xl bg-white/10" />}
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-white truncate">{p.nombre}</p>
                    <p className="text-sky-300 font-mono-num font-bold">
                      ${Number(p.precio || 0).toLocaleString("es-CL")}
                    </p>
                  </div>
                  <Plus className="w-5 h-5 text-sky-300" />
                </button>
              ))}
            </div>
            <div data-testid="kiosk-search-keyboard" className="shrink-0 mt-4 pt-4 border-t border-white/10 space-y-2">
              {["1234567890", "QWERTYUIOP", "ASDFGHJKLÑ", "ZXCVBNM"].map((fila, idx) => (
                <div key={fila} className={`flex gap-2 justify-center ${idx === 2 ? "px-4" : idx === 3 ? "px-10" : ""}`}>
                  {fila.split("").map((k) => (
                    <button key={k} data-testid={`kiosk-search-key-${k}`}
                      onClick={() => setBuscarQ((v) => (v + k).slice(0, 24))}
                      className="flex-1 max-w-[72px] h-14 rounded-xl bg-white/10 hover:bg-white/20 border border-white/10 text-white text-lg font-bold">
                      {k}
                    </button>
                  ))}
                  {idx === 3 && (
                    <button data-testid="kiosk-search-key-del" onClick={() => setBuscarQ((v) => v.slice(0, -1))}
                      className="flex-1 max-w-[150px] h-14 rounded-xl bg-rose-500/80 hover:bg-rose-500 text-white font-bold text-xs">
                      ← BORRAR
                    </button>
                  )}
                </div>
              ))}
              <div className="flex justify-center">
                <button data-testid="kiosk-search-key-space" onClick={() => setBuscarQ((v) => (v + " ").slice(0, 24))}
                  className="h-14 w-2/3 rounded-xl bg-white/10 hover:bg-white/20 border border-white/10 text-white font-bold text-xs">
                  ESPACIO
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Autorización por edad (alcohol, cigarrillos) */}
      {ageOpen && (
        <div className="fixed inset-0 bg-slate-950/95 backdrop-blur-sm z-[85] grid place-items-center p-4" data-testid="kiosk-age-modal">
          <div className="bg-slate-900 border-2 border-rose-500/40 rounded-3xl p-6 sm:p-8 max-w-lg w-full">
            <div className="text-center">
              <div className="mx-auto w-16 h-16 rounded-2xl bg-rose-500/20 grid place-items-center">
                <ShieldAlert className="w-8 h-8 text-rose-300" />
              </div>
              <h3 className="mt-3 text-2xl font-black text-white font-display">
                {lang === "es" ? "Se requiere autorización" : "Authorization required"}
              </h3>
              <p className="mt-1 text-sm text-rose-200">
                {lang === "es"
                  ? `Venta permitida solo a mayores de ${edadMinima} años por:`
                  : `Only for ${edadMinima}+ because of:`}
              </p>
            </div>
            <ul className="mt-3 rounded-2xl bg-rose-500/10 border border-rose-400/20 p-3 space-y-1">
              {itemsRestringidos.map((x) => (
                <li key={x.product_id} data-testid={`kiosk-age-item-${x.product_id}`}
                    className="text-sm font-bold text-rose-100 flex justify-between gap-2">
                  <span>{x.nombre}</span><span className="font-mono-num">×{x.cantidad}</span>
                </li>
              ))}
            </ul>

            <div className="mt-5 grid grid-cols-2 gap-2">
              <button data-testid="kiosk-age-tab-cedula" onClick={() => { setAgeMode("CEDULA"); setAgeError(""); }}
                className={`h-12 rounded-2xl font-bold text-sm ${ageMode === "CEDULA" ? "bg-sky-500 text-white" : "bg-white/5 text-slate-300 border border-white/10"}`}>
                {lang === "es" ? "Escanear mi cédula" : "Scan my ID"}
              </button>
              <button data-testid="kiosk-age-tab-supervisor" onClick={() => { setAgeMode("SUPERVISOR"); setAgeError(""); }}
                className={`h-12 rounded-2xl font-bold text-sm ${ageMode === "SUPERVISOR" ? "bg-amber-400 text-slate-900" : "bg-white/5 text-slate-300 border border-white/10"}`}>
                {lang === "es" ? "Llamar supervisor" : "Call supervisor"}
              </button>
            </div>

            {ageMode === "CEDULA" ? (
              <div className="mt-4 text-center">
                <p className="text-slate-300 text-sm">
                  {lang === "es"
                    ? "Acerca el código QR del reverso de tu cédula al lector."
                    : "Scan the QR on the back of your ID card."}
                </p>
                <input
                  data-testid="kiosk-age-scan-input"
                  autoFocus
                  value={lectorEdad.valorCrudo}
                  onChange={(e) => lectorEdad.alEscribir(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); lectorEdad.alEnter(e.target.value); } }}
                  className="mt-4 w-full h-14 rounded-2xl bg-white/5 border border-white/10 text-center text-white font-mono-num text-sm px-3"
                  placeholder={lang === "es" ? "Esperando lectura…" : "Waiting for scan…"}
                />
                {ageBusy && <p className="mt-2 text-xs text-sky-300">{lang === "es" ? "Validando…" : "Validating…"}</p>}
              </div>
            ) : (
              <div className="mt-4">
                <p className="text-center text-slate-300 text-sm">
                  {lang === "es"
                    ? "Un supervisor debe revisar la cédula y poner su PIN."
                    : "A supervisor must check the ID and enter their PIN."}
                </p>
                <p data-testid="kiosk-age-pin-display" className="mt-3 h-14 rounded-2xl bg-white/5 border border-white/10 grid place-items-center text-2xl font-mono-num tracking-[0.5em] text-white">
                  {agePin.replace(/./g, "•") || "····"}
                </p>
                <div className="mt-3 grid grid-cols-3 gap-2">
                  {["1", "2", "3", "4", "5", "6", "7", "8", "9", "C", "0", "⌫"].map((k) => (
                    <button key={k} data-testid={`kiosk-age-key-${k}`}
                      onClick={() => setAgePin((v) => k === "C" ? "" : k === "⌫" ? v.slice(0, -1) : (v + k).slice(0, 8))}
                      className="h-14 rounded-2xl bg-white/5 hover:bg-white/10 border border-white/10 text-xl font-bold text-white">
                      {k}
                    </button>
                  ))}
                </div>
                <button
                  data-testid="kiosk-age-supervisor-confirm"
                  disabled={agePin.length < 4 || ageBusy}
                  onClick={async () => {
                    setAgeBusy(true); setAgeError("");
                    try {
                      const r = await api.post(`/public/kiosk/${slug}/age-auth`,
                                               { modo: "SUPERVISOR", pin: agePin, edad_minima: edadMinima });
                      setAgeAuth({ ...r.data, pin: agePin });
                      setAgeOpen(false); setAgePin("");
                      speak(lang === "es" ? "Autorización aprobada" : "Authorization approved");
                      setTimeout(() => abrirPagoRef.current?.(), 1500);
                    } catch (err) {
                      setAgePin("");
                      setAgeError(err?.response?.data?.detail
                        || (lang === "es" ? "PIN no autorizado" : "PIN not allowed"));
                    } finally { setAgeBusy(false); }
                  }}
                  className="mt-3 w-full h-14 rounded-2xl bg-amber-400 hover:bg-amber-300 text-slate-900 font-extrabold disabled:opacity-40"
                >
                  {ageBusy ? "…" : (lang === "es" ? "Autorizar venta" : "Authorize sale")}
                </button>
              </div>
            )}

            {ageError && (
              <p data-testid="kiosk-age-error" className="mt-3 text-center text-sm font-bold text-rose-300">{ageError}</p>
            )}
            <button data-testid="kiosk-age-cancel" onClick={() => { setAgeOpen(false); setAgeError(""); }}
              className="mt-4 w-full h-12 rounded-2xl bg-white/5 border border-white/10 text-slate-300 font-bold">
              {lang === "es" ? "Volver al carro" : "Back to cart"}
            </button>
          </div>
        </div>
      )}

      {/* Modal Salir del modo autoservicio (PIN) */}
      {exitOpen && (
        <div className="fixed inset-0 bg-slate-950/95 backdrop-blur-sm z-[80] grid place-items-center p-4" data-testid="kiosk-exit-modal">
          <div className="bg-slate-900 border-2 border-rose-500/40 rounded-3xl p-6 max-w-sm w-full">
            <div className="text-center mb-4">
              <div className="mx-auto w-16 h-16 rounded-2xl bg-rose-500/20 grid place-items-center">
                <Lock className="w-8 h-8 text-rose-300" />
              </div>
              <h3 className="mt-3 text-xl font-black text-white font-display">{t.exit_title}</h3>
              <p className="text-sm text-slate-400 mt-1">{t.exit_prompt}</p>
            </div>
            <div className="bg-slate-950 rounded-2xl p-4 text-center border border-slate-800">
              <p data-testid="kiosk-exit-pin-display" className="text-4xl font-black font-mono-num text-white tracking-[0.5em] min-h-[3rem]">
                {"•".repeat(exitPin.length) || "····"}
              </p>
              {exitError && <p className="text-rose-400 text-sm font-bold mt-2">{exitError}</p>}
            </div>
            <div className="grid grid-cols-3 gap-2 mt-4">
              {["1","2","3","4","5","6","7","8","9","","0","⌫"].map((k, i) => (
                <button
                  key={i}
                  disabled={!k}
                  data-testid={k ? `kiosk-exit-key-${k === "⌫" ? "back" : k}` : undefined}
                  onClick={() => {
                    if (k === "⌫") setExitPin((s) => s.slice(0, -1));
                    else if (k) setExitPin((s) => (s + k).slice(0, 8));
                  }}
                  className={`h-14 rounded-2xl font-black text-2xl transition ${!k ? "invisible" : k === "⌫" ? "bg-rose-500 hover:bg-rose-600 text-white" : "bg-slate-800 hover:bg-slate-700 text-white"}`}
                >
                  {k}
                </button>
              ))}
            </div>
            <div className="mt-4 grid grid-cols-2 gap-2">
              <button
                onClick={() => { setExitOpen(false); setExitPin(""); setExitError(""); }}
                className="h-12 rounded-2xl border border-white/10 hover:bg-white/5 text-slate-300 font-semibold"
              >
                {t.cancel}
              </button>
              <button
                data-testid="kiosk-exit-confirm"
                onClick={async () => {
                  try {
                    await api.post(`/public/kiosk/${slug}/verify-exit`, { pin: exitPin });
                    // PIN ok → sale de fullscreen, cancela TTS, marca not-started
                    if (window.speechSynthesis) window.speechSynthesis.cancel();
                    await exitFullscreen();
                    setExitOpen(false); setExitPin(""); setExitError("");
                    // Redirige al login para que el admin salga completamente
                    window.location.href = "/";
                  } catch (_) {
                    setExitError(t.exit_wrong);
                    setExitPin("");
                  }
                }}
                className="h-12 rounded-2xl bg-rose-500 hover:bg-rose-600 text-white font-extrabold"
              >
                {t.confirm}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Aviso breve de compra aprobada: imprime directo y vuelve a la pantalla principal */}
      {ticketOpen && lastSale && (
        <div className="fixed inset-0 bg-slate-950/95 backdrop-blur-sm z-50 grid place-items-center p-6" data-testid="kiosk-ticket-modal">
          <div className="max-w-lg w-full text-center">
            <div className="mx-auto w-24 h-24 rounded-full bg-emerald-500 grid place-items-center mb-6 shadow-2xl shadow-emerald-500/30">
              <CheckCircle2 className="w-14 h-14 text-white" />
            </div>
            <p className="text-3xl sm:text-4xl font-black font-display text-white">{t.payment_approved}</p>
            <p className="mt-3 text-lg text-slate-300 flex items-center justify-center gap-2">
              <Printer className="w-5 h-5 text-sky-300" />
              {lang === "es" ? "Retira tu comprobante impreso" : "Take your printed receipt"}
            </p>
            {lastSale.numero_atencion ? (
              <div data-testid="ticket-numero-atencion" className="mt-8 rounded-3xl bg-white/5 border border-white/10 py-6">
                <p className="text-xs uppercase tracking-[0.3em] text-sky-300 font-bold">
                  {lang === "es" ? "N° de atención" : "Order number"}
                </p>
                <p className="text-[6rem] leading-none font-black font-mono-num text-white">{lastSale.numero_atencion}</p>
                <p className="text-sm text-slate-400 mt-2">
                  {lang === "es" ? "Te llamaremos por este número" : "We will call this number"}
                </p>
              </div>
            ) : null}
            <p className="mt-8 text-2xl font-bold text-amber-300">
              {lang === "es" ? "¡Gracias por tu compra!" : "Thanks for your purchase!"}
            </p>
            <p className="mt-2 text-sm text-slate-500" data-testid="kiosk-ticket-total">
              {t.total}: {clp(lastSale.total)} · {lang === "es" ? "venta" : "sale"} #{lastSale.numero}
            </p>
            <button onClick={() => { setTicketOpen(false); setStarted(false); }} data-testid="kiosk-continue-btn"
              className="mt-8 h-14 px-10 rounded-2xl bg-sky-500 hover:bg-sky-400 text-white font-bold text-lg">
              {t.continue_}
            </button>
          </div>
        </div>
      )}

      {clearAskOpen && (
        <div className="fixed inset-0 bg-slate-950/90 backdrop-blur-sm z-[60] grid place-items-center p-4" data-testid="kiosk-clear-confirm">
          <div className="bg-slate-900 border border-white/10 rounded-3xl p-6 sm:p-8 max-w-sm w-full text-center">
            <div className="mx-auto w-16 h-16 rounded-2xl bg-rose-500/15 grid place-items-center mb-4">
              <Trash2 className="w-8 h-8 text-rose-300" />
            </div>
            <p className="text-xl font-bold text-white">{t.clear_ask_title}</p>
            <p className="mt-2 text-sm text-slate-400">{t.clear_ask_body}</p>
            <div className="mt-6 grid gap-3">
              <button
                data-testid="kiosk-clear-confirm-yes"
                onClick={() => { clearCart(); setClearAskOpen(false); }}
                className="h-14 rounded-2xl bg-rose-500 hover:bg-rose-600 text-white font-bold"
              >
                {t.clear_ask_yes}
              </button>
              <button
                data-testid="kiosk-clear-confirm-no"
                onClick={() => setClearAskOpen(false)}
                className="h-14 rounded-2xl border border-white/10 hover:bg-white/5 text-slate-300 font-semibold"
              >
                {t.clear_ask_no}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Clave del menú de administración */}
      {adminPinOpen && (
        <div className="fixed inset-0 bg-slate-950/95 backdrop-blur-sm z-[85] grid place-items-center p-4" data-testid="kiosk-admin-pin-modal">
          <div className="bg-slate-900 border-2 border-sky-500/40 rounded-3xl p-6 max-w-sm w-full">
            <div className="text-center mb-4">
              <div className="mx-auto w-16 h-16 rounded-2xl bg-sky-500/20 grid place-items-center">
                <Settings className="w-8 h-8 text-sky-300" />
              </div>
              <h3 className="mt-3 text-xl font-black text-white font-display">
                {lang === "es" ? "Menú de administración" : "Admin menu"}
              </h3>
              <p className="text-sm text-slate-400 mt-1">
                {lang === "es" ? "Ingresa la clave del autoservicio" : "Enter the kiosk password"}
              </p>
            </div>
            <div className="bg-slate-950 rounded-2xl p-4 text-center border border-slate-800">
              <p data-testid="kiosk-admin-pin-display" className="text-4xl font-black font-mono-num text-white tracking-[0.5em] min-h-[3rem]">
                {"•".repeat(adminPin.length) || "····"}
              </p>
              {adminError && <p className="text-rose-400 text-sm font-bold mt-2">{adminError}</p>}
            </div>
            <div className="grid grid-cols-3 gap-2 mt-4">
              {["1","2","3","4","5","6","7","8","9","","0","⌫"].map((k, i) => (
                <button key={i} disabled={!k}
                  data-testid={k ? `kiosk-admin-key-${k === "⌫" ? "back" : k}` : undefined}
                  onClick={() => { if (k === "⌫") setAdminPin((v) => v.slice(0, -1)); else if (k) setAdminPin((v) => (v + k).slice(0, 8)); }}
                  className={`h-14 rounded-2xl font-black text-2xl transition ${!k ? "invisible" : k === "⌫" ? "bg-slate-700 hover:bg-slate-600 text-white" : "bg-slate-800 hover:bg-slate-700 text-white"}`}>
                  {k}
                </button>
              ))}
            </div>
            <div className="mt-4 grid grid-cols-2 gap-2">
              <button onClick={() => { setAdminPinOpen(false); setAdminPin(""); }}
                data-testid="kiosk-admin-pin-cancel"
                className="h-12 rounded-2xl border border-white/10 hover:bg-white/5 text-slate-300 font-semibold">
                {lang === "es" ? "Volver" : "Back"}
              </button>
              <button onClick={adminVerify} data-testid="kiosk-admin-pin-confirm"
                className="h-12 rounded-2xl bg-sky-500 hover:bg-sky-400 text-white font-extrabold">
                {t.confirm}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Menú de administración del autoservicio */}
      {adminOpen && (
        <div className="fixed inset-0 bg-slate-950/95 backdrop-blur-sm z-[85] grid place-items-center p-4 overflow-y-auto" data-testid="kiosk-admin-menu">
          <div className="bg-slate-900 border border-white/10 rounded-3xl p-6 max-w-md w-full">
            <div className="flex items-center gap-3 mb-4">
              <Settings className="w-6 h-6 text-sky-300" />
              <div>
                <h3 className="text-lg font-black text-white font-display">
                  {lang === "es" ? "Administración del autoservicio" : "Kiosk administration"}
                </h3>
                <p className="text-xs text-slate-400">{config.nombre} · {operator?.nombre}</p>
              </div>
            </div>
            {dayClose && (
              <div data-testid="kiosk-admin-dayclose-result" className="mb-4 rounded-2xl bg-emerald-500/10 border border-emerald-400/30 p-3 text-sm text-emerald-100">
                <p className="font-bold">{lang === "es" ? "Cierre de día" : "Day close"} {dayClose.fecha}</p>
                <p>{lang === "es" ? "Ventas" : "Sales"}: {dayClose.ventas} · Total ${Number(dayClose.total).toLocaleString("es-CL")}</p>
                {dayClose.tbk_cierre && (
                  <p data-testid="kiosk-admin-dayclose-tbk" className={dayClose.tbk_cierre.cierre_ok ? "text-emerald-200" : "text-amber-200 font-bold"}>
                    Transbank: {dayClose.tbk_cierre.cierre_ok ? "cierre realizado" : `cierre no realizado${dayClose.tbk_cierre.mensaje ? ` (${dayClose.tbk_cierre.mensaje})` : ""}`}
                    {dayClose.tbk_cierre.totales_ok && ` · equipo ${dayClose.tbk_cierre.cantidad_pos} / sistema ${dayClose.tbk_cierre.cantidad_sistema}`}
                  </p>
                )}
              </div>
            )}
            <div className="grid gap-2">
              <button onClick={adminDayClose} disabled={adminBusy} data-testid="kiosk-admin-day-close"
                className="h-14 rounded-2xl bg-emerald-500 hover:bg-emerald-400 text-white font-bold flex items-center justify-center gap-2 disabled:opacity-60">
                <FileText className="w-5 h-5" /> {lang === "es" ? "Cierre de día e imprimir informe" : "Day close & print report"}
              </button>
              {config.tbk_enabled && (
                <button data-testid="kiosk-admin-tbk-connect" disabled={adminBusy}
                  onClick={async () => { try { await tbkConectar(); setAviso({ tono: "info", titulo: "POS Transbank conectado" }); } catch (e) { setAviso({ tono: "error", titulo: "Transbank", mensaje: e?.message || "No se pudo conectar el POS Transbank" }); } }}
                  className="h-14 rounded-2xl bg-slate-800 hover:bg-slate-700 text-white font-bold flex items-center justify-center gap-2 disabled:opacity-60">
                  <CreditCard className="w-5 h-5" /> {lang === "es" ? "Conectar POS Transbank" : "Connect Transbank POS"}
                </button>
              )}
              <button onClick={adminReprint} disabled={adminBusy} data-testid="kiosk-admin-reprint"
                className="h-14 rounded-2xl bg-slate-800 hover:bg-slate-700 text-white font-bold flex items-center justify-center gap-2 disabled:opacity-60">
                <Printer className="w-5 h-5" /> {lang === "es" ? "Reimprimir última boleta" : "Reprint last receipt"}
              </button>
              <button onClick={() => { setAdminOpen(false); setVoiceOpen(true); }} data-testid="kiosk-admin-voices"
                className="h-14 rounded-2xl bg-slate-800 hover:bg-slate-700 text-white font-bold flex items-center justify-center gap-2">
                <Mic className="w-5 h-5" /> {lang === "es" ? "Cambio de voces" : "Change voice"}
              </button>
              <button onClick={changeOperator} data-testid="kiosk-admin-change-operator"
                className="h-14 rounded-2xl bg-slate-800 hover:bg-slate-700 text-white font-bold flex items-center justify-center gap-2">
                <UserCheck className="w-5 h-5" /> {lang === "es" ? "Cambiar operador" : "Change operator"}
              </button>
              <div className="grid grid-cols-2 gap-2">
                <button onClick={() => adminPower("restart")} disabled={adminBusy} data-testid="kiosk-admin-restart"
                  className="h-14 rounded-2xl bg-amber-500/90 hover:bg-amber-400 text-white font-bold flex items-center justify-center gap-2 disabled:opacity-60">
                  <RotateCcw className="w-5 h-5" /> {lang === "es" ? "Reiniciar" : "Restart"}
                </button>
                <button onClick={() => adminPower("shutdown")} disabled={adminBusy} data-testid="kiosk-admin-shutdown"
                  className="h-14 rounded-2xl bg-rose-600 hover:bg-rose-500 text-white font-bold flex items-center justify-center gap-2 disabled:opacity-60">
                  <Power className="w-5 h-5" /> {lang === "es" ? "Apagar" : "Shut down"}
                </button>
              </div>
              <button onClick={() => { setAdminOpen(false); setAdminPin(""); }} data-testid="kiosk-admin-back"
                className="h-12 rounded-2xl border border-white/10 hover:bg-white/5 text-slate-300 font-semibold">
                {lang === "es" ? "Volver" : "Back"}
              </button>
            </div>
            {!config.agent_enabled && (
              <p className="mt-3 text-[11px] text-slate-500 text-center">
                {lang === "es"
                  ? "Apagar y reiniciar requieren el agente de impresión Ai-POS instalado en este equipo."
                  : "Shutdown and restart require the Ai-POS local agent on this machine."}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Confirmación inmediata al agregar un producto */}
      {addedPill && (
        <div data-testid="kiosk-added-pill"
          className="fixed top-20 left-1/2 -translate-x-1/2 z-[72] pointer-events-none flex items-center gap-3 rounded-full bg-emerald-500 text-white px-6 py-3 shadow-2xl shadow-emerald-500/40 kiosk-pill">
          <CheckCircle2 className="w-6 h-6 shrink-0" />
          <span className="font-bold text-lg max-w-[60vw] truncate">{addedPill.nombre}</span>
        </div>
      )}

      {/* §13.5 · Animación "vuela al carro" */}
      {flyers.map((f) => (
        <div
          key={f.id}
          data-testid="kiosk-flyer"
          className="fixed z-[70] pointer-events-none w-20 h-20 rounded-2xl overflow-hidden ring-2 ring-sky-400 shadow-[0_0_30px_rgba(56,189,248,0.6)] kiosk-fly"
          style={{ left: f.from.x - 40, top: f.from.y - 40, "--fly-dx": `${f.dx}px`, "--fly-dy": `${f.dy}px` }}
        >
          {f.image_url ? (
            <img src={resolveMedia(f.image_url)} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-sky-500 grid place-items-center text-white">
              <ShoppingBag className="w-8 h-8" />
            </div>
          )}
        </div>
      ))}
      <style>{`
        @keyframes kioskFly {
          0%   { transform: translate(0,0) scale(1); opacity: 1; }
          60%  { opacity: 1; }
          100% { transform: translate(var(--fly-dx), var(--fly-dy)) scale(0.18) rotate(12deg); opacity: 0; }
        }
        .kiosk-fly {
          animation: kioskFly 0.8s cubic-bezier(0.3, 0.1, 0.2, 1) forwards;
        }
        @keyframes scanBeam {
          0%, 100% { transform: translateY(-14px); opacity: 0.25; }
          50%      { transform: translateY(14px); opacity: 1; }
        }
        .scan-beam { animation: scanBeam 1.6s ease-in-out infinite; }
        @keyframes kioskPill {
          0%   { transform: translate(-50%, -16px) scale(0.9); opacity: 0; }
          12%  { transform: translate(-50%, 0) scale(1); opacity: 1; }
          80%  { opacity: 1; }
          100% { transform: translate(-50%, -10px) scale(0.98); opacity: 0; }
        }
        .kiosk-pill { animation: kioskPill 1.4s ease-out forwards; }
      `}</style>
    </div>
    </>
  );

}

function KioskWelcome({ config, lang = "es", onStart }) {
  const txt = {
    autoservice: lang === "es" ? "Autoservicio" : "Self-service",
    cta: lang === "es" ? "Toca para comenzar" : "Tap to start",
    hint: lang === "es" ? "Elige tus productos y paga en la pantalla · sin filas" : "Pick your products and pay on screen · no lines",
    active: lang === "es" ? "Sistema activo" : "System active",
  };
  return (
    <div
      data-testid="kiosk-welcome"
      onClick={onStart}
      onPointerDown={(e) => { e.preventDefault(); onStart(); }}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onStart(); }}
      className="fixed inset-0 z-[70] cursor-pointer overflow-hidden bg-slate-950 text-white select-none"
    >
      {/* §13.1 · Imagen o GIF animado inicial configurado por el administrador */}
      {config.idle_media_url && (
        <>
          <img
            data-testid="kiosk-idle-media"
            src={resolveMedia(config.idle_media_url)}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-slate-950/55" />
        </>
      )}
      {/* Fondo animado con radial gradients + puntos flotantes */}
      <div className="absolute inset-0"
        style={{
          backgroundImage: config.idle_media_url ? "none" :
            "radial-gradient(circle at 20% 20%, rgba(14,165,233,0.35) 0, transparent 45%), radial-gradient(circle at 80% 60%, rgba(245,158,11,0.22) 0, transparent 40%), radial-gradient(circle at 50% 100%, rgba(16,185,129,0.20) 0, transparent 45%)",
        }}
      />
      {/* Grid overlay sutil */}
      <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "linear-gradient(rgba(255,255,255,.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.5) 1px, transparent 1px)", backgroundSize: "48px 48px" }} />

      {/* Blobs animados */}
      <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-sky-500/25 blur-3xl animate-pulse" />
      <div className="absolute -bottom-32 -right-32 w-[28rem] h-[28rem] rounded-full bg-amber-500/20 blur-3xl animate-pulse" style={{ animationDuration: "5s" }} />
      <div className="absolute top-1/3 right-1/4 w-64 h-64 rounded-full bg-emerald-500/15 blur-3xl animate-pulse" style={{ animationDuration: "7s" }} />

      {/* Contenido central */}
      <div className="relative h-full flex flex-col items-center justify-center px-6 text-center">
        {/* Logo + brand */}
        <div className="flex items-center gap-3 mb-8">
          <div className="w-16 h-16 sm:w-20 sm:h-20 grid place-items-center">
            <img src="/brand/aipos-logo.png" alt="Ai-POS" className="w-12 h-12 sm:w-16 sm:h-16" />
          </div>
          <div className="text-left">
            <p className="text-[10px] sm:text-xs uppercase tracking-[0.4em] text-sky-300 font-bold">{txt.autoservice}</p>
            <p className="text-xs sm:text-sm text-slate-400 mt-0.5">Powered by Ai-POS</p>
          </div>
        </div>

        {/* Nombre del local */}
        <h1 data-testid="kiosk-welcome-brand" className="text-5xl sm:text-7xl md:text-8xl font-black font-display leading-none tracking-tight bg-gradient-to-br from-white via-sky-100 to-amber-100 bg-clip-text text-transparent">
          {config.company_name}
        </h1>
        <p className="mt-4 text-lg sm:text-2xl text-slate-300 font-medium">
          {config.store_name}
        </p>

        {/* Divisor sparkle */}
        <div className="mt-10 flex items-center gap-3 opacity-70">
          <div className="h-px w-16 sm:w-24 bg-gradient-to-r from-transparent to-sky-400" />
          <Sparkles className="w-5 h-5 text-amber-300" />
          <div className="h-px w-16 sm:w-24 bg-gradient-to-l from-transparent to-sky-400" />
        </div>

        {/* CTA grande */}
        <div className="mt-12 flex flex-col items-center gap-4">
          <div className="relative">
            <div className="absolute inset-0 bg-sky-500 blur-2xl opacity-40 animate-pulse" />
            <div className="relative w-32 h-32 sm:w-40 sm:h-40 rounded-full bg-gradient-to-br from-sky-400 to-sky-600 grid place-items-center shadow-2xl shadow-sky-500/40 animate-bounce" style={{ animationDuration: "2s" }}>
              <Hand className="w-16 h-16 sm:w-20 sm:h-20 text-white drop-shadow-lg" strokeWidth={1.5} />
            </div>
          </div>
          <p data-testid="kiosk-welcome-cta" className="mt-4 text-2xl sm:text-4xl md:text-5xl font-black font-display uppercase tracking-wider">
            {txt.cta}
          </p>
          <p className="text-sm sm:text-base text-slate-400 mt-1">
            {txt.hint}
          </p>
        </div>

        {/* Chip inferior */}
        <div className="absolute bottom-6 inset-x-0 flex items-center justify-center gap-2 text-[10px] uppercase tracking-widest text-slate-500 font-bold">
          <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
          <span>{txt.active} · {config.nombre}</span>
        </div>
      </div>

    </div>
  );
}

/** Identificación del operador del autoservicio con PIN: las ventas quedan a su nombre. */
function KioskOperatorLogin({ config, lang = "es", api, slug, onReady }) {
  const [pin, setPin] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const txt = {
    title: lang === "es" ? "Identifícate para abrir el autoservicio" : "Identify to open the kiosk",
    sub: lang === "es" ? "Ingresa tu PIN de operador" : "Enter your operator PIN",
    wrong: lang === "es" ? "PIN no reconocido" : "PIN not recognized",
    confirm: lang === "es" ? "Entrar" : "Enter",
  };
  const submit = async () => {
    if (pin.length < 4) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/public/kiosk/${slug}/operator-login`, { pin });
      onReady({ user_id: data.user_id, nombre: data.nombre, role: data.role });
    } catch (_) {
      setErr(txt.wrong); setPin("");
    }
    setBusy(false);
  };
  return (
    <div className="min-h-screen bg-slate-950 text-white grid place-items-center p-4" data-testid="kiosk-operator-login">
      <div className="max-w-sm w-full">
        <div className="text-center mb-6">
          <div className="mx-auto w-20 h-20 grid place-items-center">
            <img src="/brand/aipos-logo.png" alt="Ai-POS" className="w-14 h-14" />
          </div>
          <p className="mt-4 text-[10px] uppercase tracking-[0.35em] text-sky-300 font-bold">
            {lang === "es" ? "Autoservicio" : "Self-service"}
          </p>
          <h1 className="text-2xl font-black font-display mt-1">{config.company_name}</h1>
          <p className="text-sm text-slate-400 mt-1">{config.store_name} · {config.nombre}</p>
          <p className="mt-4 text-base font-bold">{txt.title}</p>
          <p className="text-xs text-slate-400">{txt.sub}</p>
        </div>
        <div className="bg-slate-900 rounded-3xl border border-white/10 p-5">
          <div className="bg-slate-950 rounded-2xl p-4 text-center border border-slate-800">
            <p data-testid="kiosk-operator-pin-display" className="text-4xl font-black font-mono-num tracking-[0.5em] min-h-[3rem]">
              {"•".repeat(pin.length) || "····"}
            </p>
            {err && <p data-testid="kiosk-operator-error" className="text-rose-400 text-sm font-bold mt-2">{err}</p>}
          </div>
          <div className="grid grid-cols-3 gap-2 mt-4">
            {["1","2","3","4","5","6","7","8","9","","0","⌫"].map((k, i) => (
              <button key={i} disabled={!k}
                data-testid={k ? `kiosk-operator-key-${k === "⌫" ? "back" : k}` : undefined}
                onClick={() => { if (k === "⌫") setPin((v) => v.slice(0, -1)); else if (k) setPin((v) => (v + k).slice(0, 8)); }}
                className={`h-16 rounded-2xl font-black text-2xl transition ${!k ? "invisible" : k === "⌫" ? "bg-slate-700 hover:bg-slate-600" : "bg-slate-800 hover:bg-slate-700"}`}>
                {k}
              </button>
            ))}
          </div>
          <button onClick={submit} disabled={busy || pin.length < 4} data-testid="kiosk-operator-confirm"
            className="mt-4 w-full h-14 rounded-2xl bg-sky-500 hover:bg-sky-400 disabled:bg-slate-700 disabled:text-slate-500 font-extrabold text-lg flex items-center justify-center gap-2">
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <UserCheck className="w-5 h-5" />} {txt.confirm}
          </button>
        </div>
      </div>
    </div>
  );
}
